/**
 * View Lock Manager for Collaborative Camera Control
 *
 * Allows one user to take control of the view.
 * When locked, all other users see exactly what the controller sees:
 * - Camera position and orientation
 * - Streamlines
 * - Slices
 * - Clip planes
 * - Field selection and color maps
 * - Visibility state
 */

class ViewLockManager {
    constructor(websocket, userId, userName) {
        this.ws = websocket;
        this.userId = userId;
        this.userName = userName;

        // Lock state
        this.isLocked = false;
        this.lockHolder = null;  // { user_id, user_name }
        this.amIController = false;
        this.amIFollowing = false;  // Am I synced to the controller's view?

        // Followers tracking (for controller to know who is synced)
        this.followers = new Map();  // user_id -> { user_name }

        // Multi-user authorization state
        // When I request control: tracks who has approved/denied/pending
        this.myPendingRequest = null;  // { request_id, timestamp, approvals: { user_id: 'approved'|'denied'|'pending' } }
        // When someone else requests: tracks requests I need to respond to
        this.incomingRequests = new Map();  // request_id -> { user_id, user_name, timestamp }
        // All users in the session (for tracking who needs to approve)
        this.sessionUsers = new Map();  // user_id -> { user_name, ... }

        this.REQUEST_TIMEOUT = 60000;  // 60 seconds timeout for multi-user approval
        this.requestTimeoutTimer = null;

        // Sync throttling
        this.cameraSyncThrottle = null;
        this.stateSyncThrottle = null;
        this.CAMERA_SYNC_INTERVAL = 30; // ms - very fast for smooth camera (33fps)
        this.STATE_SYNC_INTERVAL = 150; // ms - faster for state changes

        // Flag to prevent broadcasting when applying received state
        this.isApplyingState = false;

        // Pending state to apply after data is loaded (for late joiners)
        this.pendingViewState = null;

        // UI elements
        this.toolbar = document.getElementById('viewLockToolbar');
        this.button = document.getElementById('btnViewLock');
        this.lockText = document.getElementById('viewLockText');
        this.lockIcon = document.getElementById('viewLockIcon');
        this.lockStatus = document.getElementById('viewLockStatus');
        this.lockHolderSpan = this.lockStatus?.querySelector('.view-lock-holder');

        // Authorization UI elements (created dynamically)
        this.approvalDialog = null;
        this.requestingOverlay = null;
        this.authDropdown = null;  // Dropdown showing per-user authorization status

        // Camera controls reference (set externally)
        this.cameraControls = null;
        this.camera = null;

        // Viewer reference for capturing full state
        this.viewer = null;

        this.init();
    }

    init() {
        // Show the toolbar when in collaboration mode
        if (this.toolbar) {
            this.toolbar.style.display = 'flex';
        }

        // Attach button click handler
        if (this.button) {
            this.button.addEventListener('click', () => this.toggleLock());
        }

        // Create authorization UI elements
        this.createAuthorizationUI();
    }

    /**
     * Create authorization UI elements (approval dialog, requesting overlay, and auth dropdown)
     */
    createAuthorizationUI() {
        // Create approval dialog container for when someone requests control
        this.approvalDialog = document.createElement('div');
        this.approvalDialog.className = 'view-lock-approval-dialog';
        this.approvalDialog.innerHTML = `<div class="approval-requests-list"></div>`;
        this.approvalDialog.style.display = 'none';
        document.body.appendChild(this.approvalDialog);

        // Create requesting overlay for when we're waiting for approval
        this.requestingOverlay = document.createElement('div');
        this.requestingOverlay.className = 'view-lock-requesting-overlay';
        this.requestingOverlay.innerHTML = `
            <div class="requesting-content">
                <div class="requesting-spinner"></div>
                <div class="requesting-text">Requesting control...</div>
                <div class="requesting-subtext">Waiting for all users to approve</div>
                <div class="requesting-progress">
                    <span class="approved-count">0</span> / <span class="total-count">0</span> approved
                </div>
                <div class="requesting-users-list"></div>
                <button class="requesting-cancel">Cancel Request</button>
            </div>
        `;
        this.requestingOverlay.style.display = 'none';
        document.body.appendChild(this.requestingOverlay);

        // Cancel request handler
        this.requestingOverlay.querySelector('.requesting-cancel').addEventListener('click', () => {
            this.cancelMyRequest();
        });

        // Create authorization dropdown (shown on hover over Take Control button)
        this.authDropdown = document.createElement('div');
        this.authDropdown.className = 'view-lock-auth-dropdown';
        this.authDropdown.innerHTML = `
            <div class="auth-dropdown-header">Authorization Status</div>
            <div class="auth-dropdown-list"></div>
        `;
        this.authDropdown.style.display = 'none';
        document.body.appendChild(this.authDropdown);

        // Setup hover behavior for dropdown
        this.setupDropdownHover();
    }

    /**
     * Setup hover behavior for the authorization dropdown
     */
    setupDropdownHover() {
        if (!this.button) return;

        let hoverTimeout = null;

        this.button.addEventListener('mouseenter', () => {
            // Only show dropdown if there's authorization activity
            if (this.myPendingRequest || this.sessionUsers.size > 1) {
                hoverTimeout = setTimeout(() => {
                    this.showAuthDropdown();
                }, 300);  // Small delay to avoid flicker
            }
        });

        this.button.addEventListener('mouseleave', () => {
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                hoverTimeout = null;
            }
            // Hide dropdown after a delay (allows moving to dropdown)
            setTimeout(() => {
                if (!this.authDropdown.matches(':hover')) {
                    this.hideAuthDropdown();
                }
            }, 100);
        });

        this.authDropdown.addEventListener('mouseleave', () => {
            this.hideAuthDropdown();
        });
    }

    /**
     * Show the authorization dropdown with current status
     */
    showAuthDropdown() {
        if (!this.authDropdown || !this.button) return;

        // Position dropdown below the button
        const buttonRect = this.button.getBoundingClientRect();
        this.authDropdown.style.top = `${buttonRect.bottom + 5}px`;
        this.authDropdown.style.right = `${window.innerWidth - buttonRect.right}px`;

        // Update content
        this.updateAuthDropdown();

        this.authDropdown.style.display = 'block';
    }

    /**
     * Hide the authorization dropdown
     */
    hideAuthDropdown() {
        if (this.authDropdown) {
            this.authDropdown.style.display = 'none';
        }
    }

    /**
     * Update the authorization dropdown content
     */
    updateAuthDropdown() {
        if (!this.authDropdown) return;

        const list = this.authDropdown.querySelector('.auth-dropdown-list');
        if (!list) return;

        let html = '';

        if (this.myPendingRequest) {
            // Show my outgoing request status
            html += '<div class="auth-dropdown-section">Your Request</div>';
            const approvals = this.myPendingRequest.approvals || {};

            for (const [userId, status] of Object.entries(approvals)) {
                const userName = this.sessionUsers.get(userId)?.user_name || 'User';
                const statusClass = status === 'approved' ? 'approved' :
                                   status === 'denied' ? 'denied' : 'pending';
                const statusIcon = status === 'approved' ? '✓' :
                                  status === 'denied' ? '✗' : '⏳';
                html += `
                    <div class="auth-dropdown-user ${statusClass}">
                        <span class="user-name">${this.escapeHtml(userName)}</span>
                        <span class="user-status">${statusIcon} ${status}</span>
                    </div>
                `;
            }
        } else if (this.sessionUsers.size > 1) {
            // Show users who would need to approve
            html += '<div class="auth-dropdown-section">Users in Session</div>';
            for (const [userId, userData] of this.sessionUsers) {
                if (userId === this.userId) continue;  // Skip self
                const isController = this.lockHolder?.user_id === userId;
                html += `
                    <div class="auth-dropdown-user">
                        <span class="user-name">${this.escapeHtml(userData.user_name)}</span>
                        ${isController ? '<span class="user-badge">Controller</span>' : ''}
                    </div>
                `;
            }
            if (this.sessionUsers.size <= 1) {
                html += '<div class="auth-dropdown-empty">Only you in session</div>';
            }
        } else {
            html += '<div class="auth-dropdown-empty">No other users in session</div>';
        }

        // Show incoming requests if any
        if (this.incomingRequests.size > 0) {
            html += '<div class="auth-dropdown-section">Pending Requests</div>';
            for (const [requestId, request] of this.incomingRequests) {
                html += `
                    <div class="auth-dropdown-request">
                        <span class="user-name">${this.escapeHtml(request.user_name)}</span>
                        <span class="request-status">awaiting your response</span>
                    </div>
                `;
            }
        }

        list.innerHTML = html;
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Track a user joining the session
     */
    onUserJoined(userId, userName) {
        this.sessionUsers.set(userId, { user_id: userId, user_name: userName });
        console.log('[ViewLock] User joined:', userName, 'Total users:', this.sessionUsers.size);
    }

    /**
     * Set initial presence list
     */
    setPresenceList(users) {
        this.sessionUsers.clear();
        for (const user of users) {
            this.sessionUsers.set(user.user_id, {
                user_id: user.user_id,
                user_name: user.user_name
            });
        }
        // Always include self
        if (!this.sessionUsers.has(this.userId)) {
            this.sessionUsers.set(this.userId, {
                user_id: this.userId,
                user_name: this.userName
            });
        }
        console.log('[ViewLock] Presence list set:', this.sessionUsers.size, 'users');
    }

    /**
     * Set camera controls reference for syncing
     */
    setCameraControls(controls, camera) {
        this.cameraControls = controls;
        this.camera = camera;

        // Listen for camera changes when we're the controller
        if (controls && controls.addEventListener) {
            controls.addEventListener('change', () => {
                if (this.amIController) {
                    this.broadcastCameraState();
                }
            });
        }
    }

    /**
     * Set viewer reference for full state capture
     */
    setViewer(viewer) {
        this.viewer = viewer;
    }

    /**
     * Toggle lock state
     */
    toggleLock() {
        // If we have a pending request, ignore clicks
        if (this.myPendingRequest) {
            return;
        }

        if (this.isLocked) {
            if (this.amIController) {
                // Release lock
                this.requestReleaseLock();
            } else {
                // Someone else has control - request to take it (needs approval)
                this.requestLock();
            }
        } else {
            // No one has control - take it directly
            this.requestLock();
        }
    }

    /**
     * Request to take control
     * Controller gets control immediately, other users are notified and can join
     */
    requestLock() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            // If lock already exists and we're not the controller, can't request
            if (this.isLocked && !this.amIController) {
                Utils.notify(`${this.lockHolder?.user_name || 'Someone'} currently has control. Wait for them to release.`, 'warning');
                return;
            }

            // Generate a unique request ID for tracking join requests
            const requestId = `req_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

            // Get list of other users to notify
            const otherUsers = [];
            for (const [userId] of this.sessionUsers) {
                if (userId !== this.userId) {
                    otherUsers.push(userId);
                }
            }

            // Take control immediately (no waiting)
            this.ws.send(JSON.stringify({
                type: 'lock_request',
                section_id: 'view_control',
                section_name: 'View Control',
                request_id: requestId
            }));

            // If there are other users, send them join invitations
            if (otherUsers.length > 0) {
                this.ws.send(JSON.stringify({
                    type: 'lock_auth_request',
                    section_id: 'view_control',
                    section_name: 'View Control',
                    request_id: requestId,
                    target_users: otherUsers  // All other users
                }));
                console.log('[ViewLock] Sent join invitation to', otherUsers.length, 'users');
            }
        }
    }

    /**
     * Show the requesting overlay while waiting for approval
     */
    showRequestingOverlay() {
        if (!this.requestingOverlay) return;

        // Update progress display
        this.updateRequestingOverlay();

        this.requestingOverlay.style.display = 'flex';

        // Update button to show requesting state
        if (this.lockText) {
            this.lockText.textContent = 'Requesting...';
        }
        if (this.button) {
            this.button.classList.add('requesting');
        }
    }

    /**
     * Update the requesting overlay with current approval status
     */
    updateRequestingOverlay() {
        if (!this.requestingOverlay || !this.myPendingRequest) return;

        const approvals = this.myPendingRequest.approvals || {};
        const userIds = Object.keys(approvals);
        const approvedCount = Object.values(approvals).filter(s => s === 'approved').length;
        const deniedCount = Object.values(approvals).filter(s => s === 'denied').length;

        // Update counts
        const approvedEl = this.requestingOverlay.querySelector('.approved-count');
        const totalEl = this.requestingOverlay.querySelector('.total-count');
        if (approvedEl) approvedEl.textContent = approvedCount;
        if (totalEl) totalEl.textContent = userIds.length;

        // Update user list
        const listEl = this.requestingOverlay.querySelector('.requesting-users-list');
        if (listEl) {
            let html = '';
            for (const userId of userIds) {
                const status = approvals[userId];
                const userName = this.sessionUsers.get(userId)?.user_name || 'User';
                const statusClass = status === 'approved' ? 'approved' :
                                   status === 'denied' ? 'denied' : 'pending';
                const statusIcon = status === 'approved' ? '✓' :
                                  status === 'denied' ? '✗' : '⏳';
                html += `
                    <div class="requesting-user ${statusClass}">
                        <span class="user-name">${this.escapeHtml(userName)}</span>
                        <span class="user-status">${statusIcon}</span>
                    </div>
                `;
            }
            listEl.innerHTML = html;
        }

        // Check if all users have responded (approved or denied)
        const respondedCount = approvedCount + deniedCount;
        if (respondedCount === userIds.length && userIds.length > 0) {
            // Everyone responded - proceed with whoever accepted
            this.onAllUsersResponded();
        }
    }

    /**
     * Hide the requesting overlay
     */
    hideRequestingOverlay() {
        if (this.requestingOverlay) {
            this.requestingOverlay.style.display = 'none';
        }
        if (this.button) {
            this.button.classList.remove('requesting');
        }
        this.updateUI();
    }

    /**
     * Cancel my pending request
     */
    cancelMyRequest() {
        if (this.myPendingRequest && this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
                type: 'lock_auth_cancel',
                section_id: 'view_control',
                request_id: this.myPendingRequest.request_id
            }));
        }
        this.clearMyPendingRequest();
        Utils.notify('Request cancelled', 'info');
    }

    /**
     * Clear my pending request state
     */
    clearMyPendingRequest() {
        this.myPendingRequest = null;
        if (this.requestTimeoutTimer) {
            clearTimeout(this.requestTimeoutTimer);
            this.requestTimeoutTimer = null;
        }
        this.hideRequestingOverlay();
    }

    /**
     * Handle request timeout - proceed with whoever has responded
     */
    onRequestTimeout() {
        console.log('[ViewLock] Request timed out - proceeding with current responses');
        this.onAllUsersResponded();
    }

    /**
     * Called when all users have responded (or timeout)
     * Proceed with control - users who accepted become followers, others work autonomously
     */
    onAllUsersResponded() {
        if (!this.myPendingRequest) return;

        const approvals = this.myPendingRequest.approvals || {};
        const requestId = this.myPendingRequest.request_id;

        // Count responses
        const accepted = [];
        const declined = [];
        for (const [userId, status] of Object.entries(approvals)) {
            const userName = this.sessionUsers.get(userId)?.user_name || 'User';
            if (status === 'approved') {
                accepted.push(userName);
            } else {
                declined.push(userName);  // pending or denied
            }
        }

        console.log('[ViewLock] Responses:', accepted.length, 'accepted,', declined.length, 'declined/pending');

        // Clear the pending request state
        this.clearMyPendingRequest();

        // Build notification message
        let message = '';
        if (accepted.length > 0 && declined.length === 0) {
            message = 'All users will follow your view!';
        } else if (accepted.length > 0) {
            message = `${accepted.join(', ')} will follow. ${declined.join(', ')} working independently.`;
        } else {
            message = 'No users will follow - you have control but working alone.';
        }
        Utils.notify(message, 'success');

        // Request the lock with list of followers
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            // Get user IDs of those who accepted
            const followerIds = [];
            for (const [userId, status] of Object.entries(approvals)) {
                if (status === 'approved') {
                    followerIds.push(userId);
                }
            }

            this.ws.send(JSON.stringify({
                type: 'lock_request',
                section_id: 'view_control',
                section_name: 'View Control',
                authorized_request_id: requestId,
                follower_ids: followerIds  // Users who will be synced
            }));
        }
    }

    /**
     * Handle incoming authorization request (ANY user can receive this)
     */
    onAuthorizationRequest(data) {
        // Don't process requests from ourselves
        if (data.user_id === this.userId) {
            return;
        }

        // Store the incoming request
        this.incomingRequests.set(data.request_id, {
            user_id: data.user_id,
            user_name: data.user_name,
            request_id: data.request_id,
            timestamp: Date.now()
        });

        // Update the approval dialog
        this.updateApprovalDialog();

        // Show approval dialog if not already visible
        if (this.approvalDialog.style.display !== 'flex') {
            this.approvalDialog.style.display = 'flex';
            this.playNotificationSound();
        }

        console.log('[ViewLock] Received authorization request from', data.user_name);
    }

    /**
     * Get initials from a user name
     */
    getInitials(name) {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    }

    /**
     * Generate a consistent color for a user based on their name
     */
    getUserColor(name) {
        const colors = [
            '#4285F4', // Blue
            '#EA4335', // Red
            '#FBBC04', // Yellow
            '#34A853', // Green
            '#9334E6', // Purple
            '#E91E63', // Pink
            '#00BCD4', // Cyan
            '#FF5722', // Deep Orange
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }

    /**
     * Update the approval dialog to show all incoming requests
     */
    updateApprovalDialog() {
        if (!this.approvalDialog) return;

        const listEl = this.approvalDialog.querySelector('.approval-requests-list');
        if (!listEl) return;

        let html = '';
        for (const [requestId, request] of this.incomingRequests) {
            const initials = this.getInitials(request.user_name);
            const avatarColor = this.getUserColor(request.user_name);
            html += `
                <div class="approval-request-card" data-request-id="${requestId}">
                    <div class="request-card-content">
                        <div class="request-avatar" style="background: ${avatarColor}">
                            ${initials}
                        </div>
                        <div class="request-message">
                            <span class="requester-name">${this.escapeHtml(request.user_name)}</span>
                            <span class="request-action-text">is requesting view control</span>
                        </div>
                    </div>
                    <div class="request-buttons">
                        <button class="request-btn deny" data-request-id="${requestId}">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                            Deny
                        </button>
                        <button class="request-btn approve" data-request-id="${requestId}">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            Allow
                        </button>
                    </div>
                </div>
            `;
        }

        if (this.incomingRequests.size === 0) {
            this.approvalDialog.style.display = 'none';
        }

        listEl.innerHTML = html;

        // Attach event handlers
        listEl.querySelectorAll('.request-btn.approve').forEach(btn => {
            btn.addEventListener('click', () => {
                this.respondToRequest(btn.dataset.requestId, true);
            });
        });
        listEl.querySelectorAll('.request-btn.deny').forEach(btn => {
            btn.addEventListener('click', () => {
                this.respondToRequest(btn.dataset.requestId, false);
            });
        });
    }

    /**
     * Hide approval dialog
     */
    hideApprovalDialog() {
        if (this.approvalDialog) {
            this.approvalDialog.style.display = 'none';
        }
    }

    /**
     * Play a notification sound for incoming requests
     */
    playNotificationSound() {
        try {
            // Create a simple beep using Web Audio API
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioCtx.destination);

            oscillator.frequency.value = 800;
            oscillator.type = 'sine';
            gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);

            oscillator.start(audioCtx.currentTime);
            oscillator.stop(audioCtx.currentTime + 0.3);
        } catch (e) {
            // Audio not available, ignore
        }
    }

    /**
     * Respond to a specific authorization request
     * @param {string} requestId - The request ID to respond to
     * @param {boolean} approved - true to approve, false to deny
     */
    respondToRequest(requestId, approved) {
        const request = this.incomingRequests.get(requestId);
        if (!request || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            return;
        }

        this.ws.send(JSON.stringify({
            type: 'lock_auth_response',
            section_id: 'view_control',
            request_id: requestId,
            target_user_id: request.user_id,
            approved: approved
        }));

        console.log('[ViewLock] Responded to request from', request.user_name, ':', approved ? 'approved' : 'denied');

        // Remove from incoming requests
        this.incomingRequests.delete(requestId);
        this.updateApprovalDialog();

        // Show confirmation
        Utils.notify(
            approved ? `Approved ${request.user_name}'s request` : `Denied ${request.user_name}'s request`,
            approved ? 'success' : 'info'
        );
    }

    /**
     * Handle authorization response (when someone declines to join sync)
     */
    onAuthorizationResponse(data) {
        console.log('[ViewLock] Received authorization response:', data);

        // Only matters if someone declined - approvals are handled via follower_joined
        if (!data.approved && this.amIController) {
            // Someone declined to join - just log it, no notification needed
            console.log('[ViewLock]', data.user_name, 'declined to join sync');
        }
    }

    /**
     * Handle when a request is cancelled by the requester
     */
    onAuthorizationCancelled(data) {
        // Remove from incoming requests if we had it
        if (this.incomingRequests.has(data.request_id)) {
            this.incomingRequests.delete(data.request_id);
            this.updateApprovalDialog();
            Utils.notify(`${data.user_name} cancelled their request`, 'info');
        }
    }

    /**
     * Request to release control
     */
    requestReleaseLock() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            // Broadcast final state before releasing so followers keep the view
            this.broadcastFinalState();

            this.ws.send(JSON.stringify({
                type: 'lock_release',
                section_id: 'view_control'
            }));
        }
    }

    /**
     * Broadcast final state immediately (no throttle) before releasing lock
     */
    broadcastFinalState() {
        if (!this.amIController) return;

        const state = this.captureFullState();
        if (!state) return;

        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
                type: 'view_state_sync',
                state: state,
                timestamp: Date.now(),
                isFinal: true  // Mark as final state before release
            }));
        }
    }

    /**
     * Handle lock granted message
     */
    onLockGranted(data) {
        if (data.section === 'view_control') {
            this.isLocked = true;
            this.amIController = true;
            this.lockHolder = {
                user_id: this.userId,
                user_name: this.userName
            };

            // Store followers list
            const followerIds = data.follower_ids || [];
            this.followers.clear();
            for (const fId of followerIds) {
                const userInfo = this.sessionUsers.get(fId);
                if (userInfo) {
                    this.followers.set(fId, { user_name: userInfo.user_name });
                }
            }

            this.updateUI();

            // Broadcast full state immediately when taking control
            this.broadcastFullState();

            // Start continuous camera broadcasting
            this.startCameraBroadcast();
        }
    }

    /**
     * Handle follower joined message (someone started following)
     */
    onFollowerJoined(userId, userName, data) {
        console.log('[ViewLock] onFollowerJoined:', userId, userName, data, 'myId:', this.userId);

        if (data.section === 'view_control') {
            // Update follower_ids from the message
            const followerIds = data.follower_ids || [];
            this.followers.clear();
            for (const fId of followerIds) {
                const userInfo = this.sessionUsers.get(fId);
                if (userInfo) {
                    this.followers.set(fId, { user_name: userInfo.user_name });
                }
            }

            // If I'm the one who joined, set my following state
            if (userId === this.userId) {
                console.log('[ViewLock] I joined the sync! Setting amIFollowing = true');
                this.amIFollowing = true;

                // Slow down preloading while following to prioritize sync updates
                if (this.viewer && this.viewer.geometryCache) {
                    console.log('[ViewLock] Reducing preload priority while following sync');
                    this.viewer.geometryCache.preloadDelay = 1000; // 1 second between preloads
                }

                // Apply pending view state if we have one
                if (this.pendingViewState && this.viewer?.currentData) {
                    console.log('[ViewLock] Applying pending view state after joining sync');
                    setTimeout(() => {
                        this.applyFullState(this.pendingViewState);
                        this.pendingViewState = null;
                    }, 100);
                }
            }

            console.log('[ViewLock] State after follower_joined: isLocked=', this.isLocked, 'amIFollowing=', this.amIFollowing, 'amIController=', this.amIController);
            this.updateUI();

            if (this.amIController) {
                Utils.notify(`${userName} joined the sync`, 'success');
                // Send current state to the new follower
                console.log('[ViewLock] Broadcasting full state to new follower');
                this.broadcastFullState(true);  // Force immediate broadcast
            } else if (userId === this.userId) {
                Utils.notify(`You are now synced with ${this.lockHolder?.user_name || 'the controller'}`, 'success');
            }
        }
    }

    /**
     * Handle follower left message (someone stopped following)
     */
    onFollowerLeft(userId, userName, data) {
        if (data.section === 'view_control') {
            // Update followers list
            this.followers.delete(userId);

            // Update follower_ids from the message
            const followerIds = data.follower_ids || [];
            this.followers.clear();
            for (const fId of followerIds) {
                const userInfo = this.sessionUsers.get(fId);
                if (userInfo) {
                    this.followers.set(fId, { user_name: userInfo.user_name });
                }
            }

            this.updateUI();

            if (this.amIController) {
                Utils.notify(`${userName} stopped following`, 'info');
            }
        }
    }

    /**
     * Handle lock denied message
     */
    onLockDenied(data) {
        if (data.section === 'view_control') {
            Utils.notify(data.reason || 'Could not acquire view control', 'warning');
        }
    }

    /**
     * Handle lock taken by another user
     */
    onLockTaken(userId, userName, data) {
        if (data.section === 'view_control') {
            this.isLocked = true;
            this.amIController = false;
            this.lockHolder = {
                user_id: userId,
                user_name: userName
            };

            // Check if I'm in the followers list
            const followerIds = data.follower_ids || [];
            this.amIFollowing = followerIds.includes(this.userId);

            // Store followers list (for controller info)
            this.followers.clear();
            for (const fId of followerIds) {
                const userInfo = this.sessionUsers.get(fId);
                if (userInfo) {
                    this.followers.set(fId, { user_name: userInfo.user_name });
                }
            }

            this.updateUI();

            if (this.amIFollowing) {
                Utils.notify(`${userName} is now controlling the view - you are synced`, 'info');
            } else {
                Utils.notify(`${userName} is now controlling the view - you are working independently`, 'info');
            }
        }
    }

    /**
     * Handle lock released
     */
    onLockReleased(userId, userName, data) {
        if (data.section === 'view_control') {
            // Check if this was a transfer (control taken by someone else via authorization)
            const wasTransferred = data.transferred_to;
            const wasMe = userId === this.userId;

            this.isLocked = false;
            this.amIController = false;
            this.amIFollowing = false;
            this.lockHolder = null;
            this.followers.clear();
            this.updateUI();
            this.stopCameraBroadcast();

            // Restore normal preload speed when sync ends
            if (this.viewer && this.viewer.geometryCache) {
                this.viewer.geometryCache.preloadDelay = 250;
            }

            if (wasTransferred) {
                if (wasMe) {
                    Utils.notify(`Control transferred to ${data.transferred_to}`, 'info');
                }
                // Don't notify others - they'll get the lock_taken message
            } else {
                Utils.notify(`${userName} released view control`, 'info');
            }
        }
    }

    /**
     * Handle initial locks list
     */
    onLocksList(locks) {
        console.log('[ViewLock] onLocksList received:', locks);
        if (locks.view_control) {
            this.isLocked = true;
            this.lockHolder = {
                user_id: locks.view_control.user_id,
                user_name: locks.view_control.user_name
            };
            this.amIController = (locks.view_control.user_id === this.userId);
            console.log('[ViewLock] onLocksList - isLocked:', this.isLocked, 'amIController:', this.amIController, 'amIFollowing:', this.amIFollowing, 'myId:', this.userId, 'holderId:', this.lockHolder.user_id);
            this.updateUI();

            if (this.amIController) {
                this.startCameraBroadcast();
            }
        }
    }

    /**
     * Update UI based on lock state
     */
    updateUI() {
        console.log('[ViewLock] updateUI called - isLocked:', this.isLocked, 'amIController:', this.amIController, 'amIFollowing:', this.amIFollowing);
        if (!this.button || !this.lockText || !this.lockStatus) return;

        // Get or create leave sync button
        let leaveSyncBtn = document.getElementById('btnLeaveSync');

        if (this.isLocked) {
            if (this.amIController) {
                console.log('[ViewLock] updateUI: I am the controller');
                // I'm controlling
                this.button.classList.add('active', 'controlling');
                this.button.classList.remove('locked-by-other', 'following', 'requesting');
                this.lockText.textContent = 'Release Control';
                this.lockIcon.innerHTML = '<path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z"/>';

                // Show follower count (controller doesn't need "is controlling" text)
                if (this.followers.size > 0) {
                    const names = Array.from(this.followers.values()).map(f => f.user_name).join(', ');
                    this.lockStatus.style.display = 'inline';
                    // Set entire innerHTML to avoid the "is controlling" suffix
                    this.lockStatus.innerHTML = `<span class="view-lock-holder" title="${names}">${this.followers.size} following</span>`;
                } else {
                    this.lockStatus.style.display = 'none';
                }

                // Hide leave sync button and join sync button for controller
                if (leaveSyncBtn) leaveSyncBtn.style.display = 'none';
                const joinSyncBtn = document.getElementById('btnJoinSync');
                if (joinSyncBtn) joinSyncBtn.style.display = 'none';

            } else if (this.amIFollowing) {
                console.log('[ViewLock] updateUI: I am following');
                // I'm following the controller
                this.button.classList.remove('active', 'controlling', 'locked-by-other', 'requesting');
                this.button.classList.add('following');
                this.lockText.textContent = 'Following';
                // Eye icon for following
                this.lockIcon.innerHTML = '<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>';
                this.lockStatus.style.display = 'inline';
                // Show who is controlling
                const holderName = this.lockHolder?.user_name || 'Someone';
                this.lockStatus.innerHTML = `<span class="view-lock-holder">${holderName}</span> is controlling`;

                // Disable main button when following
                this.button.disabled = true;

                // Show leave sync button, hide join sync button
                this.showLeaveSyncButton();
                const joinSyncBtnFollow = document.getElementById('btnJoinSync');
                if (joinSyncBtnFollow) joinSyncBtnFollow.style.display = 'none';

            } else {
                console.log('[ViewLock] updateUI: I am working independently - showing Join Sync button');
                // Someone else is controlling but I'm working independently
                this.button.classList.remove('active', 'controlling', 'following', 'requesting');
                this.button.classList.add('locked-by-other');
                this.lockText.textContent = 'Working Independently';
                // Person icon for independent
                this.lockIcon.innerHTML = '<path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>';
                this.lockStatus.style.display = 'inline';
                // Show who has control
                const independentHolderName = this.lockHolder?.user_name || 'Someone';
                this.lockStatus.innerHTML = `<span class="view-lock-holder">${independentHolderName}</span> has control`;

                // Disable main button when someone else has control
                this.button.disabled = true;

                // Show join sync button instead of leave sync
                if (leaveSyncBtn) leaveSyncBtn.style.display = 'none';
                this.showJoinSyncButton();
            }
        } else {
            // No lock
            this.button.classList.remove('active', 'controlling', 'locked-by-other', 'following', 'requesting');
            this.button.disabled = false;
            this.lockText.textContent = 'Take Control';
            this.lockIcon.innerHTML = '<path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>';
            this.lockStatus.style.display = 'none';

            // Hide leave sync button and join sync button when no lock
            if (leaveSyncBtn) leaveSyncBtn.style.display = 'none';
            const joinSyncBtn = document.getElementById('btnJoinSync');
            if (joinSyncBtn) joinSyncBtn.style.display = 'none';
        }
    }

    /**
     * Show the Leave Sync button for followers
     */
    showLeaveSyncButton() {
        let leaveSyncBtn = document.getElementById('btnLeaveSync');

        if (!leaveSyncBtn && this.toolbar) {
            // Create the button
            leaveSyncBtn = document.createElement('button');
            leaveSyncBtn.id = 'btnLeaveSync';
            leaveSyncBtn.className = 'view-lock-leave-btn';
            leaveSyncBtn.innerHTML = `
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
                </svg>
                <span>Leave Sync</span>
            `;
            leaveSyncBtn.title = 'Stop following and work independently';
            leaveSyncBtn.addEventListener('click', () => this.leaveSync());
            this.toolbar.appendChild(leaveSyncBtn);
        }

        if (leaveSyncBtn) {
            leaveSyncBtn.style.display = 'flex';
        }
    }

    /**
     * Leave the synchronized session (stop following)
     */
    leaveSync() {
        if (!this.amIFollowing) return;

        this.amIFollowing = false;
        this.updateUI();

        // Restore normal preload speed
        if (this.viewer && this.viewer.geometryCache) {
            this.viewer.geometryCache.preloadDelay = 250;
        }

        // Notify the server
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
                type: 'leave_sync',
                section_id: 'view_control'
            }));
        }

        Utils.notify('You are now working independently', 'info');
    }

    /**
     * Show the Join Sync button for users working independently
     */
    showJoinSyncButton() {
        console.log('[ViewLock] showJoinSyncButton called');
        let joinSyncBtn = document.getElementById('btnJoinSync');

        if (!joinSyncBtn && this.toolbar) {
            // Create the button
            joinSyncBtn = document.createElement('button');
            joinSyncBtn.id = 'btnJoinSync';
            joinSyncBtn.className = 'view-lock-join-btn';
            joinSyncBtn.innerHTML = `
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                </svg>
                <span>Join Sync</span>
            `;
            joinSyncBtn.title = 'Join the synchronized view session';
            joinSyncBtn.addEventListener('click', () => this.joinSync());
            this.toolbar.appendChild(joinSyncBtn);
        }

        if (joinSyncBtn) {
            joinSyncBtn.style.display = 'flex';
        }
    }

    /**
     * Join the synchronized session (start following the controller)
     */
    joinSync() {
        console.log('[ViewLock] joinSync called. isLocked:', this.isLocked, 'lockHolder:', this.lockHolder);
        if (!this.isLocked || !this.lockHolder) {
            console.log('[ViewLock] joinSync aborted - no lock or lockHolder');
            return;
        }

        // Send approval to join the sync
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            const requestId = `join_${Date.now()}`;
            const message = {
                type: 'lock_auth_response',
                section_id: 'view_control',
                request_id: requestId,
                target_user_id: this.lockHolder.user_id,
                approved: true
            };
            console.log('[ViewLock] Sending join sync message:', message);
            this.ws.send(JSON.stringify(message));
        } else {
            console.log('[ViewLock] joinSync aborted - WebSocket not open');
        }

        Utils.notify(`Joining sync with ${this.lockHolder.user_name}...`, 'info');
    }

    /**
     * Start broadcasting camera state
     */
    startCameraBroadcast() {
        this.broadcastCameraState();
    }

    /**
     * Stop broadcasting
     */
    stopCameraBroadcast() {
        if (this.cameraSyncThrottle) {
            clearTimeout(this.cameraSyncThrottle);
            this.cameraSyncThrottle = null;
        }
        if (this.stateSyncThrottle) {
            clearTimeout(this.stateSyncThrottle);
            this.stateSyncThrottle = null;
        }
    }

    /**
     * Broadcast current camera state to all users
     */
    broadcastCameraState() {
        if (!this.amIController || !this.camera) return;

        // Throttle broadcasts
        if (this.cameraSyncThrottle) return;

        this.cameraSyncThrottle = setTimeout(() => {
            this.cameraSyncThrottle = null;

            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                const cameraState = {
                    position: {
                        x: this.camera.position.x,
                        y: this.camera.position.y,
                        z: this.camera.position.z
                    },
                    target: this.cameraControls ? {
                        x: this.cameraControls.target.x,
                        y: this.cameraControls.target.y,
                        z: this.cameraControls.target.z
                    } : null,
                    up: {
                        x: this.camera.up.x,
                        y: this.camera.up.y,
                        z: this.camera.up.z
                    },
                    zoom: this.camera.zoom || 1
                };

                this.ws.send(JSON.stringify({
                    type: 'camera_sync',
                    camera: cameraState,
                    timestamp: Date.now()
                }));
            }
        }, this.CAMERA_SYNC_INTERVAL);
    }

    /**
     * Broadcast full visualization state
     * Called when taking control or making significant changes
     * @param {boolean} forceImmediate - If true, bypass throttle and send immediately
     */
    broadcastFullState(forceImmediate = false) {
        if (!this.amIController) return;

        // Force immediate send (used for important state changes like clearing measurements)
        if (forceImmediate) {
            // Cancel any pending throttled update
            if (this.stateSyncThrottle) {
                clearTimeout(this.stateSyncThrottle);
                this.stateSyncThrottle = null;
            }
            this.pendingStateUpdate = false;

            const state = this.captureFullState();
            if (!state) return;

            this.stateSequence = (this.stateSequence || 0) + 1;

            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({
                    type: 'view_state_sync',
                    state: state,
                    timestamp: Date.now(),
                    sequence: this.stateSequence
                }));
                console.log('[ViewLock] State broadcast sent (immediate), seq:', this.stateSequence, 'measurements:', state.measurements?.items?.length || 0);
            }
            return;
        }

        // Mark that a state update is pending
        this.pendingStateUpdate = true;

        // Throttle state broadcasts - but always send the LATEST state when timer fires
        if (this.stateSyncThrottle) return;

        this.stateSyncThrottle = setTimeout(() => {
            this.stateSyncThrottle = null;

            // Only send if there's a pending update
            if (!this.pendingStateUpdate) return;
            this.pendingStateUpdate = false;

            const state = this.captureFullState();
            if (!state) return;

            // Add sequence number for ordering
            this.stateSequence = (this.stateSequence || 0) + 1;

            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({
                    type: 'view_state_sync',
                    state: state,
                    timestamp: Date.now(),
                    sequence: this.stateSequence
                }));
                console.log('[ViewLock] State broadcast sent, seq:', this.stateSequence, 'measurements:', state.measurements?.items?.length || 0);
            }
        }, this.STATE_SYNC_INTERVAL);
    }

    /**
     * Capture complete visualization state
     */
    captureFullState() {
        if (!this.viewer) return null;

        const state = {
            camera: null,
            field: null,
            colorMap: null,
            scalarRange: null,
            representation: null,
            opacity: null,
            timestep: null,
            filters: [],  // Active filters (streamlines, slices, clips, contours, glyphs)
            visibility: {}
        };

        // Camera state
        if (this.camera) {
            state.camera = {
                position: {
                    x: this.camera.position.x,
                    y: this.camera.position.y,
                    z: this.camera.position.z
                },
                target: this.cameraControls ? {
                    x: this.cameraControls.target.x,
                    y: this.cameraControls.target.y,
                    z: this.cameraControls.target.z
                } : null,
                up: {
                    x: this.camera.up.x,
                    y: this.camera.up.y,
                    z: this.camera.up.z
                },
                zoom: this.camera.zoom || 1
            };
        }

        // Active field from colormap controls
        const fieldSelect = document.getElementById('colormapVariable');
        if (fieldSelect && fieldSelect.value) {
            state.field = fieldSelect.value;
        }

        // Color map preset
        const colorMapSelect = document.getElementById('colormapPreset');
        if (colorMapSelect && colorMapSelect.value) {
            state.colorMap = colorMapSelect.value;
        }

        // Scalar range from inputs
        const minInput = document.getElementById('colormapMin');
        const maxInput = document.getElementById('colormapMax');
        if (minInput && maxInput) {
            state.scalarRange = {
                min: parseFloat(minInput.value) || 0,
                max: parseFloat(maxInput.value) || 1
            };
        }

        // Representation mode (surface, wireframe, points)
        if (this.viewer.displaySettings && this.viewer.displaySettings.representation) {
            state.representation = this.viewer.displaySettings.representation;
        }

        // Opacity
        if (this.viewer.displaySettings && typeof this.viewer.displaySettings.opacity === 'number') {
            state.opacity = this.viewer.displaySettings.opacity;
        }

        // Light and shadow levels
        if (this.viewer.displaySettings) {
            if (typeof this.viewer.displaySettings.lightLevel === 'number') {
                state.lightLevel = this.viewer.displaySettings.lightLevel;
            }
            if (typeof this.viewer.displaySettings.shadowLevel === 'number') {
                state.shadowLevel = this.viewer.displaySettings.shadowLevel;
            }
        }

        // Current timestep
        if (typeof this.viewer.currentTimestep === 'number') {
            state.timestep = this.viewer.currentTimestep;
        }

        // Capture active filters from the Filters class
        if (this.viewer.filters && this.viewer.filters.activeFilters) {
            console.log('[ViewLock] Capturing filters, activeFilters size:', this.viewer.filters.activeFilters.size,
                'keys:', Array.from(this.viewer.filters.activeFilters.keys()));
            this.viewer.filters.activeFilters.forEach((filterData, filterId) => {
                // Capture visibility from mesh
                const isVisible = filterData.mesh ? filterData.mesh.visible : true;

                const filterState = {
                    id: filterId,
                    type: filterData.type,
                    visible: isVisible,
                    options: filterData.options ? { ...filterData.options } : {}
                };

                // For streamlines, capture the stored options which contain seed parameters
                if (filterData.type === 'streamlines' && filterData.options) {
                    filterState.options = { ...filterData.options };
                }

                // For contours, properties are stored at top level, not inside options
                // Also capture the auto-spacing setting from UI
                if (filterData.type === 'contour') {
                    const autoSpacing = document.getElementById('contourAutoSpacing')?.checked || false;
                    filterState.options = {
                        field: filterData.field,
                        autoSpacing: autoSpacing,
                        numContours: filterData.numContours
                    };
                    // Only include values if NOT using auto-spacing (manual mode)
                    if (!autoSpacing && filterData.contourValues) {
                        filterState.options.values = filterData.contourValues;
                    }
                    // Include cache_key for collaborative session cache lookup
                    if (filterData.cache_key) {
                        filterState.options.cache_key = filterData.cache_key;
                    }
                }

                console.log('[ViewLock] Captured filter:', filterId, 'type:', filterData.type, 'options:', filterState.options);
                state.filters.push(filterState);
            });
        }

        // Mesh part visibility
        if (this.viewer.meshParts && this.viewer.meshParts.visibleParts) {
            state.visibility.meshParts = Array.from(this.viewer.meshParts.visibleParts);
        }

        // Auto-rotation state
        if (this.viewer.autoRotation) {
            state.autoRotation = {
                isActive: this.viewer.autoRotation.isActive,
                speed: this.viewer.autoRotation.speed,
                duration: this.viewer.autoRotation.duration,
                continuous: this.viewer.autoRotation.continuous,
                // Capture current rotation axis and center for smooth sync
                rotationAxis: this.viewer.autoRotation.rotationAxis ? {
                    x: this.viewer.autoRotation.rotationAxis.x,
                    y: this.viewer.autoRotation.rotationAxis.y,
                    z: this.viewer.autoRotation.rotationAxis.z
                } : null,
                meshCenter: this.viewer.autoRotation.meshCenter ? {
                    x: this.viewer.autoRotation.meshCenter.x,
                    y: this.viewer.autoRotation.meshCenter.y,
                    z: this.viewer.autoRotation.meshCenter.z
                } : null
            };
        }

        // Always capture probe points (even without plot data)
        if (this.viewer?.probePoints) {
            state.probePoints = [...this.viewer.probePoints];
        }

        // Capture current plot data if any
        if (window.currentPlotData) {
            state.plotData = {
                ...window.currentPlotData,
                // Capture UI state for the plot
                field: document.getElementById('xyPlotField')?.value,
                probeType: document.getElementById('xyPlotProbeType')?.value || 'line',
                numPoints: parseInt(document.getElementById('xyPlotNumPoints')?.value) || 100,
                // Capture probe line coordinates if line probe
                lineStart: {
                    x: parseFloat(document.getElementById('probeLineStartX')?.value) || 0,
                    y: parseFloat(document.getElementById('probeLineStartY')?.value) || 0,
                    z: parseFloat(document.getElementById('probeLineStartZ')?.value) || 0
                },
                lineEnd: {
                    x: parseFloat(document.getElementById('probeLineEndX')?.value) || 0,
                    y: parseFloat(document.getElementById('probeLineEndY')?.value) || 0,
                    z: parseFloat(document.getElementById('probeLineEndZ')?.value) || 0
                },
                // Capture slice plane parameters
                slicePlane: {
                    normalX: parseFloat(document.getElementById('slicePlaneNormalX')?.value) || 0,
                    normalY: parseFloat(document.getElementById('slicePlaneNormalY')?.value) || 0,
                    normalZ: parseFloat(document.getElementById('slicePlaneNormalZ')?.value) || 1,
                    originX: parseFloat(document.getElementById('slicePlaneOriginX')?.value) || 0,
                    originY: parseFloat(document.getElementById('slicePlaneOriginY')?.value) || 0,
                    originZ: parseFloat(document.getElementById('slicePlaneOriginZ')?.value) || 0,
                    resolution: parseInt(document.getElementById('slicePlaneResolution')?.value) || 100
                },
                // Capture probe points for point cloud mode
                probePoints: this.viewer?.probePoints ? [...this.viewer.probePoints] : [],
                // Check if modal is open (explicitly check for 'block')
                modalOpen: document.getElementById('chartModal')?.style.display === 'block'
            };
        }

        // Capture integration results if any
        if (window.currentIntegrationData) {
            const integrationEnabled = document.getElementById('integrationEnabled')?.checked;
            const resultsVisible = document.getElementById('integrationResults')?.style.display === 'block';

            state.integrationData = {
                ...window.currentIntegrationData,
                // Capture UI state
                enabled: integrationEnabled,
                field: document.getElementById('integrationField')?.value,
                integrationType: document.getElementById('integrationType')?.value || 'surface',
                surfaceSelection: document.getElementById('integrationSurface')?.value || 'all',
                resultsVisible: resultsVisible
            };
        }

        // Capture mass flow rate results if any
        if (window.currentMassFlowData) {
            const massFlowEnabled = document.getElementById('massFlowEnabled')?.checked;
            const resultsVisible = document.getElementById('massFlowResults')?.style.display === 'block';

            state.massFlowData = {
                ...window.currentMassFlowData,
                enabled: massFlowEnabled,
                velocityField: document.getElementById('massFlowVelocityField')?.value,
                densityMode: document.getElementById('massFlowDensityMode')?.value || 'constant',
                densityValue: parseFloat(document.getElementById('massFlowDensityValue')?.value) || 1.225,
                densityField: document.getElementById('massFlowDensityFieldSelect')?.value,
                surfaceSelection: document.getElementById('massFlowSurface')?.value || 'all',
                resultsVisible: resultsVisible
            };
        }

        // Capture force coefficients results if any
        if (window.currentForceCoeffData) {
            const forceCoeffEnabled = document.getElementById('forceCoeffEnabled')?.checked;
            const resultsVisible = document.getElementById('forceCoeffResults')?.style.display === 'block';

            state.forceCoeffData = {
                ...window.currentForceCoeffData,
                enabled: forceCoeffEnabled,
                pressureField: document.getElementById('forceCoeffPressureField')?.value,
                velocityField: document.getElementById('forceCoeffVelocityField')?.value,
                refVelocity: parseFloat(document.getElementById('forceCoeffRefVelocity')?.value) || null,
                refArea: parseFloat(document.getElementById('forceCoeffRefArea')?.value) || null,
                refDensity: parseFloat(document.getElementById('forceCoeffRefDensity')?.value) || 1.225,
                refPressure: parseFloat(document.getElementById('forceCoeffRefPressure')?.value) || 0,
                flowDirection: [
                    parseFloat(document.getElementById('forceCoeffFlowX')?.value) || 1,
                    parseFloat(document.getElementById('forceCoeffFlowY')?.value) || 0,
                    parseFloat(document.getElementById('forceCoeffFlowZ')?.value) || 0
                ],
                liftDirection: [
                    parseFloat(document.getElementById('forceCoeffLiftX')?.value) || 0,
                    parseFloat(document.getElementById('forceCoeffLiftY')?.value) || 1,
                    parseFloat(document.getElementById('forceCoeffLiftZ')?.value) || 0
                ],
                flipNormals: document.getElementById('forceCoeffFlipNormals')?.checked || false,
                surfaceSelection: document.getElementById('forceCoeffSurface')?.value || 'all',
                resultsVisible: resultsVisible
            };
        }

        // Capture heat transfer results if any
        if (window.currentHeatTransferData) {
            const heatTransferEnabled = document.getElementById('heatTransferEnabled')?.checked;
            const resultsVisible = document.getElementById('heatTransferResults')?.style.display === 'block';

            state.heatTransferData = {
                ...window.currentHeatTransferData,
                enabled: heatTransferEnabled,
                fluxField: document.getElementById('heatTransferFluxField')?.value,
                tempField: document.getElementById('heatTransferTempField')?.value,
                refTemp: parseFloat(document.getElementById('heatTransferRefTemp')?.value) || 300,
                wallTemp: parseFloat(document.getElementById('heatTransferWallTemp')?.value) || null,
                surfaceSelection: document.getElementById('heatTransferSurface')?.value || 'all',
                resultsVisible: resultsVisible
            };
        }

        // Capture mesh cutter state if enabled
        if (this.viewer?.filters?.meshCutterPlanes) {
            const meshCutterEnabled = document.getElementById('meshCutterEnabled')?.checked;
            const meshCutterConfig = this.viewer.filters.getMeshCutterConfig();

            state.meshCutterData = {
                enabled: meshCutterEnabled,
                planes: meshCutterConfig.planes || [],
                widgetStates: meshCutterConfig.widgetStates || {},  // Include visible, clippingActive states
                data: meshCutterConfig.data,
                resultsVisible: document.getElementById('meshCutterResults')?.style.display === 'block'
            };
        }

        // Capture mesh eraser state if enabled
        if (this.viewer?.filters?.meshEraser) {
            const meshEraserEnabled = document.getElementById('meshEraserEnabled')?.checked;
            const meshEraserConfig = this.viewer.filters.getMeshEraserConfig();

            if (meshEraserConfig) {
                state.meshEraserData = {
                    enabled: meshEraserEnabled,
                    brushRadiusPercent: meshEraserConfig.brushRadiusPercent,
                    sequences: meshEraserConfig.sequences,
                    resultsVisible: document.getElementById('meshEraserResults')?.style.display !== 'none'
                };
            }
        }

        // Capture mesh solidify state if enabled
        if (this.viewer?.filters?.meshSolidify) {
            const meshSolidifyEnabled = document.getElementById('meshSolidifyEnabled')?.checked;
            const solidifyThickness = parseFloat(document.getElementById('solidifyThicknessValue')?.value || '1');
            const solidifyDirection = document.getElementById('solidifyDirection')?.value || 'both';

            // Collect selected parts from checkboxes
            const solidifyParts = [];
            document.querySelectorAll('#solidifyPartsList input[type="checkbox"]').forEach(cb => {
                if (cb.checked) solidifyParts.push(cb.dataset.partName);
            });

            state.meshSolidifyData = {
                enabled: meshSolidifyEnabled,
                active: this.viewer.filters.meshSolidify.active,
                thickness: solidifyThickness,
                direction: solidifyDirection,
                selectedParts: solidifyParts,
                resultsVisible: document.getElementById('solidifyResults')?.style.display !== 'none'
            };
        }

        // Capture measurements (always include to sync clearing)
        // Note: this.viewer.measurements is always an array (even if empty)
        if (this.viewer?.measurements !== undefined) {
            const measurementEnabled = document.getElementById('measurementEnabled')?.checked;
            const measurementUnit = document.getElementById('measurementUnit')?.value || 'm';
            const measurementShow = document.getElementById('measurementShow')?.checked;

            console.log('[ViewLock] Capturing measurements, count:', this.viewer.measurements.length);

            state.measurements = {
                enabled: measurementEnabled,
                unit: measurementUnit,
                visible: measurementShow,
                items: this.viewer.measurements.map(m => ({
                    id: m.id,
                    type: m.type,
                    color: m.color,
                    // Distance measurement
                    startPoint: m.startPoint,
                    endPoint: m.endPoint,
                    distance: m.distance,
                    // Angle measurement
                    point1: m.point1,
                    vertex: m.vertex,
                    point2: m.point2,
                    angle: m.angle,
                    // Area/Volume measurement
                    points: m.points,
                    area: m.area,
                    volume: m.volume
                }))
            };
        }

        // Capture annotations (always include to sync clearing)
        if (this.viewer?.annotationRenderer) {
            const annotationEnabled = document.getElementById('annotationsEnabled')?.checked;
            const annotations = this.viewer.annotationRenderer.getAnnotations();

            console.log('[ViewLock] Capturing annotations, count:', annotations.length);

            state.annotations = {
                enabled: annotationEnabled,
                items: annotations.map(a => ({
                    id: a.id,
                    type: a.type,
                    // Text annotation
                    text: a.text,
                    position: a.position,
                    font_size: a.font_size,
                    color: a.color,
                    background_color: a.background_color,
                    background_opacity: a.background_opacity,
                    // Arrow annotation
                    start_point: a.start_point,
                    end_point: a.end_point,
                    arrow_size: a.arrow_size,
                    line_width: a.line_width,
                    // Scale bar
                    length: a.length,
                    unit: a.unit,
                    num_divisions: a.num_divisions,
                    // Coordinate axes
                    origin: a.origin,
                    labels: a.labels,
                    colors: a.colors
                }))
            };
        }

        return state;
    }

    /**
     * Handle incoming camera sync from controller
     */
    onCameraSync(userId, cameraState) {
        // Only apply if we're following the controller
        if (this.amIController || !this.isLocked || !this.amIFollowing) return;

        // Only apply from the lock holder
        if (this.lockHolder && this.lockHolder.user_id !== userId) return;

        this.applyCameraState(cameraState);
    }

    /**
     * Handle incoming full state sync from controller
     */
    onViewStateSync(userId, state, sequence) {
        // Ignore if I'm the controller
        if (this.amIController) return;

        // Only apply from the lock holder (if we have one)
        if (this.lockHolder && this.lockHolder.user_id !== userId) return;

        // Check sequence to ignore out-of-order messages
        console.log('[ViewLock] Received state sync, seq:', sequence, 'last:', this.lastReceivedSequence);
        if (sequence !== undefined) {
            if (this.lastReceivedSequence !== undefined && sequence <= this.lastReceivedSequence) {
                console.log('[ViewLock] Ignoring out-of-order state sync, seq:', sequence, 'last:', this.lastReceivedSequence);
                return;
            }
            this.lastReceivedSequence = sequence;
        } else {
            console.warn('[ViewLock] State sync received without sequence number!');
        }

        // Always store the latest state (for when user joins sync later)
        this.pendingViewState = state;

        // If not following, just store - don't apply yet
        if (!this.isLocked || !this.amIFollowing) {
            console.log('[ViewLock] Not following, storing state for later');
            return;
        }

        // Check if data is loaded - if not, it will be applied via applyPendingViewState
        if (!this.viewer?.currentData) {
            console.log('[ViewLock] Data not loaded yet, state stored for later');
            return;
        }

        this.applyFullState(state);
        this.pendingViewState = null;  // Clear after applying
    }

    /**
     * Apply pending view state after data is loaded
     * Called from viewer after case is loaded
     */
    applyPendingViewState() {
        if (this.pendingViewState) {
            console.log('[ViewLock] Applying pending view state after data load');
            // Small delay to ensure UI is ready
            setTimeout(() => {
                this.applyFullState(this.pendingViewState);
                this.pendingViewState = null;
            }, 500);
        }
    }

    /**
     * Apply camera state from controller
     */
    applyCameraState(cameraState) {
        if (!this.camera || !cameraState) return;

        if (cameraState.position) {
            this.camera.position.set(
                cameraState.position.x,
                cameraState.position.y,
                cameraState.position.z
            );
        }

        if (cameraState.target && this.cameraControls) {
            this.cameraControls.target.set(
                cameraState.target.x,
                cameraState.target.y,
                cameraState.target.z
            );
        }

        // Apply up vector for TrackballControls (allows free rotation)
        if (cameraState.up) {
            this.camera.up.set(
                cameraState.up.x,
                cameraState.up.y,
                cameraState.up.z
            );
        }

        if (cameraState.zoom && this.camera.zoom !== undefined) {
            this.camera.zoom = cameraState.zoom;
            this.camera.updateProjectionMatrix();
        }

        if (this.cameraControls && this.cameraControls.update) {
            this.cameraControls.update();
        }
    }

    /**
     * Apply full visualization state from controller
     */
    async applyFullState(state) {
        if (!state || !this.viewer) return;

        // Set flag to prevent broadcasting while applying state
        this.isApplyingState = true;
        console.log('[ViewLock] Applying full state:', state);

        // Apply camera
        if (state.camera) {
            this.applyCameraState(state.camera);
        }

        const isGeometryMode = this.viewer.renderingMode === 'geometry';

        // Apply field selection
        if (state.field) {
            const fieldSelect = document.getElementById('colormapVariable');
            if (fieldSelect && fieldSelect.value !== state.field) {
                fieldSelect.value = state.field;
                // In geometry mode, field change triggers reloadGeometry which handles colors
                fieldSelect.dispatchEvent(new Event('change'));
            }
        }

        // Apply color map preset
        let colormapChanged = false;
        if (state.colorMap) {
            const colorMapSelect = document.getElementById('colormapPreset');
            if (colorMapSelect && colorMapSelect.value !== state.colorMap) {
                colorMapSelect.value = state.colorMap;
                if (this.viewer.colormap) {
                    this.viewer.colormap.preset = state.colorMap;
                }
                colormapChanged = true;
                // In non-geometry mode, dispatch change event
                if (!isGeometryMode) {
                    colorMapSelect.dispatchEvent(new Event('change'));
                }
            }
        }

        // Apply scalar range
        if (state.scalarRange) {
            const minInput = document.getElementById('colormapMin');
            const maxInput = document.getElementById('colormapMax');
            if (minInput && maxInput) {
                minInput.value = state.scalarRange.min;
                maxInput.value = state.scalarRange.max;
                // Update internal colormap state
                if (this.viewer.colormap) {
                    this.viewer.colormap.min = state.scalarRange.min;
                    this.viewer.colormap.max = state.scalarRange.max;
                }
                colormapChanged = true;
                // In non-geometry mode, dispatch change event
                if (!isGeometryMode) {
                    minInput.dispatchEvent(new Event('change'));
                }
            }
        }

        // In geometry mode, recolor the mesh directly after updating colormap settings
        if (isGeometryMode && colormapChanged && this.viewer.engine?.recolorGeometry) {
            const colormapSettings = this.viewer.getColormapSettings ? this.viewer.getColormapSettings() : null;
            if (colormapSettings) {
                console.log('[ViewLock] Recoloring geometry with synced colormap:', colormapSettings);
                this.viewer.engine.recolorGeometry(colormapSettings);
            }
        }

        // Apply representation mode (surface, wireframe, points)
        if (state.representation) {
            const repSelect = document.getElementById('representationType');
            if (repSelect) {
                repSelect.value = state.representation;
            }
            // Update displaySettings and call viewer method directly
            if (this.viewer.displaySettings) {
                this.viewer.displaySettings.representation = state.representation;
            }
            if (this.viewer.updateRepresentation) {
                this.viewer.updateRepresentation();
            }
            console.log('[ViewLock] Applied representation:', state.representation);
        }

        // Apply opacity
        if (typeof state.opacity === 'number') {
            // Update UI elements if they exist
            const opacitySlider = document.getElementById('opacitySlider');
            const opacityValue = document.getElementById('opacityValue');
            if (opacitySlider) {
                opacitySlider.value = Math.round(state.opacity * 100);
            }
            if (opacityValue) {
                opacityValue.textContent = `${Math.round(state.opacity * 100)}%`;
            }

            // Always apply opacity to the viewer, regardless of UI elements
            if (this.viewer.displaySettings) {
                this.viewer.displaySettings.opacity = state.opacity;
            }
            if (this.viewer.updateOpacity) {
                this.viewer.updateOpacity();
                console.log('[ViewLock] Applied opacity:', state.opacity);
            }
        }

        // Apply light and shadow levels
        if (typeof state.lightLevel === 'number') {
            const lightSlider = document.getElementById('lightLevelSlider');
            const lightValue = document.getElementById('lightLevelValue');
            if (lightSlider) lightSlider.value = Math.round(state.lightLevel * 100);
            if (lightValue) lightValue.textContent = `${Math.round(state.lightLevel * 100)}%`;
            if (this.viewer.displaySettings) {
                this.viewer.displaySettings.lightLevel = state.lightLevel;
            }
        }
        if (typeof state.shadowLevel === 'number') {
            const shadowSlider = document.getElementById('shadowLevelSlider');
            const shadowValue = document.getElementById('shadowLevelValue');
            if (shadowSlider) shadowSlider.value = Math.round(state.shadowLevel * 100);
            if (shadowValue) shadowValue.textContent = `${Math.round(state.shadowLevel * 100)}%`;
            if (this.viewer.displaySettings) {
                this.viewer.displaySettings.shadowLevel = state.shadowLevel;
            }
        }
        if (typeof state.lightLevel === 'number' || typeof state.shadowLevel === 'number') {
            if (this.viewer.updateLighting) {
                this.viewer.updateLighting();
            }
        }

        // Apply timestep
        if (typeof state.timestep === 'number' && this.viewer.currentTimestep !== state.timestep) {
            // Use the viewer's method to change timestep
            if (this.viewer.setCurrentTimestep) {
                this.viewer.setCurrentTimestep(state.timestep);
            }
            // Also update the time slider UI
            const timeSlider = document.getElementById('timeSlider');
            if (timeSlider) {
                timeSlider.value = state.timestep;
                timeSlider.dispatchEvent(new Event('input'));
            }
        }

        // Apply mesh part visibility BEFORE filters (filters use mesh_parts in cache key computation)
        if (state.visibility && state.visibility.meshParts) {
            this.applyMeshVisibility(state.visibility.meshParts);
        }

        // Apply auto-rotation state
        if (state.autoRotation && this.viewer.autoRotation) {
            const ar = state.autoRotation;
            const currentlyActive = this.viewer.autoRotation.isActive;

            // Update settings
            this.viewer.autoRotation.speed = ar.speed;
            this.viewer.autoRotation.duration = ar.duration;
            this.viewer.autoRotation.continuous = ar.continuous;

            // Update UI sliders
            const speedSlider = document.getElementById('rotateSpeedSlider');
            const speedValue = document.getElementById('rotateSpeedValue');
            const durationSlider = document.getElementById('rotateDurationSlider');
            const durationValue = document.getElementById('rotateDurationValue');
            const continuousToggle = document.getElementById('rotateContinuousToggle');
            const toggleTrack = document.getElementById('rotateToggleTrack');
            const toggleKnob = document.getElementById('rotateToggleKnob');

            if (speedSlider) speedSlider.value = ar.speed;
            if (speedValue) speedValue.textContent = `${ar.speed} deg/s`;
            if (durationSlider) {
                durationSlider.value = ar.duration;
                durationSlider.disabled = ar.continuous;
                durationSlider.style.opacity = ar.continuous ? '0.5' : '1';
            }
            if (durationValue) durationValue.textContent = `${ar.duration} s`;
            if (continuousToggle) continuousToggle.checked = ar.continuous;
            if (toggleKnob) toggleKnob.style.transform = ar.continuous ? 'translateX(18px)' : 'translateX(0)';
            if (toggleTrack) toggleTrack.style.backgroundColor = ar.continuous ? 'var(--apple-accent, #30d158)' : '#0a0a0a';

            // Sync rotation UI state (but don't start local animation - camera position is synced)
            if (ar.isActive && !currentlyActive) {
                // Controller is rotating - update UI to show rotation active
                // Camera position comes from continuous sync, not local animation
                this.viewer.autoRotation.isActive = true;

                // Update button UI
                const btnStartRotation = document.getElementById('btnStartRotation');
                if (btnStartRotation) {
                    btnStartRotation.innerHTML = `
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                            <rect x="4" y="4" width="12" height="12" rx="2"/>
                        </svg>
                        Stop Rotation
                    `;
                    btnStartRotation.style.background = 'var(--apple-danger, #ff453a)';
                }
                const btnRotateView = document.getElementById('btnRotateView');
                if (btnRotateView) {
                    btnRotateView.style.background = 'rgba(48, 209, 88, 0.3)';
                }
                console.log('[ViewLock] Auto-rotation active (synced from controller)');
            } else if (!ar.isActive && currentlyActive) {
                // Controller stopped rotation - update UI
                this.viewer.autoRotation.isActive = false;

                // Update button UI
                const btnStartRotation = document.getElementById('btnStartRotation');
                if (btnStartRotation) {
                    btnStartRotation.innerHTML = `
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                            <polygon points="5,3 17,10 5,17"/>
                        </svg>
                        Start Rotation
                    `;
                    btnStartRotation.style.background = 'var(--apple-accent, #30d158)';
                }
                const btnRotateView = document.getElementById('btnRotateView');
                if (btnRotateView) {
                    btnRotateView.style.background = '';
                }
                console.log('[ViewLock] Auto-rotation stopped (synced from controller)');
            }
        }

        // Apply filters (streamlines, slices, clips, contours, glyphs)
        if (state.filters && this.viewer.filters) {
            await this.applyFiltersState(state.filters);
        }

        // Apply plot data if present, or close modal if plot was cleared
        if (state.plotData) {
            await this.applyPlotData(state.plotData);
        } else if (window.currentPlotData) {
            // Controller closed the plot, close it on follower too
            if (typeof closeChartModal === 'function') {
                closeChartModal();
            }
        }

        // Apply probe points from top-level state (handles case when no plot data yet)
        if (state.probePoints !== undefined && this.viewer) {
            const currentPoints = this.viewer.probePoints || [];
            const newPoints = state.probePoints || [];

            // Only update if different
            if (JSON.stringify(currentPoints) !== JSON.stringify(newPoints)) {
                if (this.viewer.clearProbePoints) {
                    this.viewer.clearProbePoints();
                }
                newPoints.forEach(point => {
                    if (this.viewer.addProbePoint) {
                        // Pass true to skip sync - we're applying received state
                        this.viewer.addProbePoint(point, true);
                    }
                });
                console.log('[ViewLock] Applied probe points from state:', newPoints.length);
            }
        }

        // Apply integration data if present, or clear if controller cleared it
        if (state.integrationData) {
            await this.applyIntegrationData(state.integrationData);
        } else if (window.currentIntegrationData) {
            // Controller cleared integration, clear on follower too
            window.currentIntegrationData = null;
            const resultsDiv = document.getElementById('integrationResults');
            if (resultsDiv) resultsDiv.style.display = 'none';
            const exportBtn = document.getElementById('btnExportIntegrationCSV');
            if (exportBtn) exportBtn.style.display = 'none';
        }

        // Apply mass flow rate data if present, or clear if controller cleared it
        if (state.massFlowData) {
            await this.applyMassFlowData(state.massFlowData);
        } else if (window.currentMassFlowData) {
            window.currentMassFlowData = null;
            const resultsDiv = document.getElementById('massFlowResults');
            if (resultsDiv) resultsDiv.style.display = 'none';
        }

        // Apply force coefficients data if present, or clear if controller cleared it
        if (state.forceCoeffData) {
            await this.applyForceCoeffData(state.forceCoeffData);
        } else if (window.currentForceCoeffData) {
            window.currentForceCoeffData = null;
            const resultsDiv = document.getElementById('forceCoeffResults');
            if (resultsDiv) resultsDiv.style.display = 'none';
        }

        // Apply heat transfer data if present, or clear if controller cleared it
        if (state.heatTransferData) {
            await this.applyHeatTransferData(state.heatTransferData);
        } else if (window.currentHeatTransferData) {
            window.currentHeatTransferData = null;
            const resultsDiv = document.getElementById('heatTransferResults');
            if (resultsDiv) resultsDiv.style.display = 'none';
        }

        // Apply mesh cutter data if present, or clear if controller cleared it
        if (state.meshCutterData) {
            await this.applyMeshCutterData(state.meshCutterData);
        } else if (window.currentMeshCutterData) {
            window.currentMeshCutterData = null;
            if (this.viewer?.filters) {
                this.viewer.filters.clearCuttingPlanes();
            }
            const resultsDiv = document.getElementById('meshCutterResults');
            if (resultsDiv) resultsDiv.style.display = 'none';
        }

        // Apply mesh eraser data if present, or clear if controller cleared it
        if (state.meshEraserData) {
            await this.applyMeshEraserData(state.meshEraserData);
        } else if (this.viewer?.filters?.meshEraser?.sequences?.length > 0) {
            this.viewer.filters.meshEraser.clearAllSequences();
        }

        // Apply mesh solidify data if present, or clear if controller cleared it
        if (state.meshSolidifyData) {
            await this.applyMeshSolidifyData(state.meshSolidifyData);
        } else if (this.viewer?.filters?.meshSolidify?.active) {
            this.viewer.filters.meshSolidify.remove();
            const solidifyEnabled = document.getElementById('meshSolidifyEnabled');
            if (solidifyEnabled) solidifyEnabled.checked = false;
            const solidifyControls = document.getElementById('meshSolidifyControls');
            if (solidifyControls) solidifyControls.style.display = 'none';
            const solidifyResults = document.getElementById('solidifyResults');
            if (solidifyResults) solidifyResults.style.display = 'none';
        }

        // Apply measurements if present, or clear if controller cleared them
        if (state.measurements) {
            await this.applyMeasurements(state.measurements);
        } else if (this.viewer?.measurements?.length > 0) {
            // Controller cleared measurements, clear on follower too
            if (this.viewer.clearAllMeasurements) {
                this.viewer.clearAllMeasurements();
                this.viewer.updateMeasurementsList();
            }
        }

        // Apply annotations if present, or clear if controller cleared them
        if (state.annotations) {
            await this.applyAnnotations(state.annotations);
        } else if (this.viewer?.annotationRenderer) {
            // Controller cleared annotations, clear on follower too
            const annotations = this.viewer.annotationRenderer.getAnnotations();
            if (annotations.length > 0) {
                this.viewer.annotationRenderer.clearAll();
                this.viewer.updateAnnotationsList();
            }
        }

        // Clear the flag after applying state
        this.isApplyingState = false;
    }

    /**
     * Apply filters state - recreate filters on follower's view
     */
    async applyFiltersState(filters) {
        if (!this.viewer.filters) return;

        console.log('[ViewLock] applyFiltersState called with', filters?.length || 0, 'filters:',
            filters?.map(f => `${f.id}(${f.type})`).join(', '));

        const filtersClass = this.viewer.filters;
        const currentFilterIds = new Set(filtersClass.activeFilters.keys());
        const newFilterIds = new Set(filters.map(f => f.id));

        // Remove filters that no longer exist
        for (const existingId of currentFilterIds) {
            if (!newFilterIds.has(existingId)) {
                // Get filter type before removing to update UI
                const filterEntry = filtersClass.activeFilters.get(existingId);
                const filterType = filterEntry?.type;

                console.log('[ViewLock] Removing filter:', existingId, 'type:', filterType);
                filtersClass.removeFilter(existingId);

                // Update UI checkbox to unchecked state
                if (filterType) {
                    this.updateFilterCheckbox(filterType, false);
                }

                // Hide slice legend when slice_contours is removed
                if (filterType === 'slice_contours' && this.viewer && this.viewer.hideSliceLegend) {
                    console.log('[ViewLock] Hiding slice legend after removing slice_contours');
                    this.viewer.hideSliceLegend();

                    // Also remove the slice mesh from scene and disable the slice widget
                    const sliceWidget = this.viewer.filters?.sliceWidget;
                    if (sliceWidget) {
                        // Remove the slice mesh from renderer
                        const prefix = sliceWidget.config?.inputPrefix || 'slice';
                        const slicePlaneObjectName = `${prefix}-plane`;
                        if (sliceWidget.renderer) {
                            sliceWidget.renderer.removeObject(slicePlaneObjectName);
                            console.log('[ViewLock] Removed slice mesh from renderer:', slicePlaneObjectName);
                        }
                    }
                }

                // When all slice filters are removed, check if we should disable the slice widget entirely
                if (existingId === 'slice-contours' || filterType === 'slice_contours') {
                    // Check if the main slice filter checkbox should be unchecked
                    const sliceFilterCheckbox = document.getElementById('sliceFilterEnabled');
                    const sliceWidget = this.viewer.filters?.sliceWidget;

                    // If no more slice-related filters exist in the new state, disable the widget
                    const hasSliceFilters = filters.some(f =>
                        f.type === 'slice_contours' || f.type === 'slice_vectors' || f.type === 'slice_isolines'
                    );

                    if (!hasSliceFilters && sliceWidget && sliceWidget.enabled) {
                        console.log('[ViewLock] No slice filters remaining, disabling slice widget');
                        sliceWidget.disable();
                        if (sliceFilterCheckbox) {
                            sliceFilterCheckbox.checked = false;
                            const sliceControls = document.getElementById('sliceControls');
                            if (sliceControls) sliceControls.style.display = 'none';
                        }
                    }
                }
            }
        }

        // Add or update filters
        for (const filterState of filters) {
            const { id, type, options, visible } = filterState;

            // Check if filter already exists
            const existing = filtersClass.activeFilters.get(id);
            const shouldBeVisible = visible !== false;

            // If filter exists, check if we just need to update visibility
            if (existing) {
                // Check if options match - if so, just update visibility
                let optionsMatch = false;
                if (type === 'contour') {
                    optionsMatch = existing.field === options.field &&
                                   existing.numContours === options.numContours;
                } else {
                    optionsMatch = this.filterOptionsMatch(existing.options, options);
                }

                if (optionsMatch) {
                    // Just update visibility, no need to recreate
                    console.log('[ViewLock] Filter options match, updating visibility only:', id, shouldBeVisible);
                    this.setFilterVisibility(existing, type, shouldBeVisible);
                    this.updateFilterCheckbox(type, shouldBeVisible);
                    continue;
                }

                // Options changed - remove old filter before recreating
                console.log('[ViewLock] Filter options changed, removing old filter:', id);
                filtersClass.removeFilter(id);
            }

            console.log('[ViewLock] Creating filter:', id, type, options);

            try {
                switch (type) {
                    case 'streamlines':
                        await this.recreateStreamlines(id, options);
                        break;
                    case 'slice':
                        await this.recreateSlice(id, options);
                        break;
                    case 'contour':
                        await this.recreateContour(id, options);
                        break;
                    case 'glyph':
                        await this.recreateGlyph(id, options);
                        break;
                    case 'surface_lic':
                        await this.recreateSurfaceLIC(id, options);
                        break;
                    case 'volume_lic':
                        await this.recreateVolumeLIC(id, options);
                        break;
                    case 'slice_contours':
                        await this.recreateSliceContours(id, options);
                        break;
                    case 'slice_vectors':
                        await this.recreateSliceVectors(id, options);
                        break;
                    case 'slice_isolines':
                        await this.recreateSliceIsolines(id, options);
                        break;
                    default:
                        console.log('[ViewLock] Unknown filter type:', type);
                }

                // Apply visibility state from controller
                const filterEntry = this.viewer.filters.activeFilters.get(id);
                if (filterEntry) {
                    this.setFilterVisibility(filterEntry, type, shouldBeVisible);
                    console.log('[ViewLock] Set filter visible:', id, shouldBeVisible);
                }
                // Update UI checkbox
                this.updateFilterCheckbox(type, shouldBeVisible);
            } catch (err) {
                console.error('[ViewLock] Failed to recreate filter:', id, err);
            }
        }

        // Request a render to show visibility changes
        if (this.viewer.engine && this.viewer.engine.render) {
            this.viewer.engine.render();
        }
    }

    /**
     * Update filter UI checkbox to match visibility state
     */
    updateFilterCheckbox(filterType, isVisible) {
        let checkboxId = null;
        let controlsId = null;

        switch (filterType) {
            case 'contour':
                checkboxId = 'contourFilterEnabled';
                controlsId = 'contourControls';
                break;
            case 'streamlines':
                checkboxId = 'streamlineFilterEnabled';
                controlsId = 'streamlineControls';
                break;
            case 'slice':
                checkboxId = 'sliceFilterEnabled';
                controlsId = 'sliceControls';
                break;
            case 'glyph':
                checkboxId = 'glyphFilterEnabled';
                controlsId = 'glyphControls';
                break;
            case 'surface_lic':
                checkboxId = 'surfaceLICFilterEnabled';
                controlsId = 'surfaceLICControls';
                break;
            case 'volume_lic':
                checkboxId = 'volumeLICFilterEnabled';
                controlsId = 'volumeLICControls';
                break;
            case 'slice_contours':
                checkboxId = 'sliceShowContours';
                controlsId = 'sliceContourSettings';
                break;
            case 'slice_vectors':
                checkboxId = 'sliceShowVectors';
                controlsId = 'sliceVectorSettings';
                break;
            case 'slice_isolines':
                checkboxId = 'sliceShowIsolines';
                controlsId = 'sliceIsolinesSettings';
                break;
        }

        if (checkboxId) {
            const checkbox = document.getElementById(checkboxId);
            if (checkbox) {
                checkbox.checked = isVisible;
            }
            // Also update controls visibility
            if (controlsId) {
                const controls = document.getElementById(controlsId);
                if (controls) {
                    controls.style.display = isVisible ? 'flex' : 'none';
                }
            }
        }
    }

    /**
     * Set visibility for a filter (mesh and labels)
     */
    setFilterVisibility(filterEntry, filterType, isVisible) {
        // Update mesh visibility
        if (filterEntry.mesh) {
            filterEntry.mesh.visible = isVisible;
        }
        // For Surface LIC, use the renderer's setVisible method
        if (filterType === 'surface_lic' && filterEntry.renderer && filterEntry.renderer.setVisible) {
            filterEntry.renderer.setVisible(isVisible);
        }
        // For Volume LIC, use the renderer's setVisible method
        if (filterType === 'volume_lic' && filterEntry.renderer && filterEntry.renderer.setVisible) {
            filterEntry.renderer.setVisible(isVisible);
        }
        // For contours, also update label visibility
        if (filterType === 'contour' && filterEntry.labels && Array.isArray(filterEntry.labels)) {
            filterEntry.labels.forEach(label => {
                label.style.display = isVisible ? 'block' : 'none';
            });
        }
        // For streamlines, update legend visibility
        if (filterType === 'streamlines') {
            const legend = document.getElementById('streamlineLegend');
            if (legend) {
                legend.style.display = isVisible ? 'block' : 'none';
            }
        }
        // For glyphs, update legend visibility
        if (filterType === 'glyph') {
            const legend = document.getElementById('glyphLegend');
            if (legend) {
                legend.style.display = isVisible ? 'flex' : 'none';
            }
        }
        // For slice_contours, update slice legend visibility
        if (filterType === 'slice_contours') {
            if (isVisible) {
                // Legend is shown when contours are created, no action needed here
            } else {
                // Hide the slice legend when slice contours are hidden
                if (this.viewer && this.viewer.hideSliceLegend) {
                    this.viewer.hideSliceLegend();
                }
            }
        }
    }

    /**
     * Check if filter options match (to avoid unnecessary recreation)
     */
    filterOptionsMatch(options1, options2) {
        if (!options1 || !options2) return false;
        // Simple comparison - could be more sophisticated
        return JSON.stringify(options1) === JSON.stringify(options2);
    }

    /**
     * Recreate streamlines filter
     */
    async recreateStreamlines(id, options) {
        if (!this.viewer.filters || !this.viewer.currentData) return;

        // Handle slice-streamlines (2D streamlines on slice plane) separately
        if (id === 'slice-streamlines') {
            await this.recreateSliceStreamlines(options);
            return;
        }

        // Regular 3D streamlines
        // Ensure streamline filter is enabled (checkbox checked)
        const streamlineCheckbox = document.getElementById('streamlineFilterEnabled');
        if (streamlineCheckbox && !streamlineCheckbox.checked) {
            streamlineCheckbox.checked = true;
            // Show the controls panel
            const controls = document.getElementById('streamlineControls');
            if (controls) controls.style.display = 'block';
        }

        // Remove existing if present
        this.viewer.filters.removeFilter(id);

        // Populate UI inputs from options (for display purposes)
        this.populateStreamlineUI(options);

        // IMPORTANT: Use createStreamlines directly with the captured options
        // instead of applyStreamlinesFromUI which would regenerate new seed points
        if (options && options.seedPoints && options.seedPoints.length > 0) {
            console.log('[ViewLock] Creating streamlines directly with captured seedPoints:', options.seedPoints.length);

            // Create streamlines with the exact same options from the controller
            const streamOptions = {
                ...options,
                id: id
            };

            try {
                await this.viewer.filters.createStreamlines(this.viewer.currentData, streamOptions);
                console.log('[ViewLock] Streamlines created successfully with synced seed points');
            } catch (err) {
                console.error('[ViewLock] Failed to create streamlines:', err);
            }
        } else {
            // Fallback: if no seedPoints, use UI-based creation
            console.log('[ViewLock] No seedPoints in options, falling back to applyStreamlinesFromUI');
            await this.viewer.filters.applyStreamlinesFromUI();
        }
    }

    /**
     * Recreate slice-streamlines (2D streamlines on slice plane)
     */
    async recreateSliceStreamlines(options) {
        if (!this.viewer.filters || !this.viewer.currentData) {
            console.warn('[ViewLock] Cannot recreate slice-streamlines - no filters or data');
            return;
        }

        // Remove existing slice-streamlines
        this.viewer.filters.removeFilter('slice-streamlines');

        // Ensure the slice streamlines checkbox is enabled
        const sliceStreamlinesCheckbox = document.getElementById('sliceShowStreamlines');
        if (sliceStreamlinesCheckbox && !sliceStreamlinesCheckbox.checked) {
            sliceStreamlinesCheckbox.checked = true;
            // Show the streamline settings panel
            const settings = document.getElementById('sliceStreamlineSettings');
            if (settings) settings.style.display = 'block';
        }

        // If we have seedPoints in the options, directly create the streamlines
        // This ensures we get the exact same streamlines as the controller
        if (options && options.seedPoints && options.seedPoints.length > 0) {
            console.log('[ViewLock] Creating slice-streamlines directly with captured seedPoints');

            // Ensure the ID is set correctly for slice-streamlines
            const streamOptions = {
                ...options,
                id: 'slice-streamlines',
                skipSeedHelper: true,
                previewEnabled: false,
                showSeedGizmo: false,
                renderMode: 'lines'
            };

            try {
                await this.viewer.filters.createStreamlines(this.viewer.currentData, streamOptions);
                console.log('[ViewLock] Slice-streamlines created successfully');
            } catch (err) {
                console.error('[ViewLock] Failed to create slice-streamlines:', err);
            }
        } else {
            // Fallback: try to use the slice widget if available
            const sliceWidget = this.viewer.filters?.sliceWidget;
            if (sliceWidget) {
                console.log('[ViewLock] No seedPoints, using slice widget to generate slice-streamlines');
                this.populateSliceStreamlineUI(options);
                await sliceWidget.applySliceStreamlines();
            } else {
                console.warn('[ViewLock] No seedPoints and no slice widget available');
            }
        }
    }

    /**
     * Recreate slice-contours (colored contours on slice plane)
     */
    async recreateSliceContours(id, options) {
        if (!this.viewer.filters) {
            console.warn('[ViewLock] Cannot recreate slice-contours - no filters');
            return;
        }

        // Remove existing slice-contours
        this.viewer.filters.removeFilter('slice-contours');

        // Get the slice widget
        const sliceWidget = this.viewer.filters?.sliceWidget;
        if (!sliceWidget) {
            console.warn('[ViewLock] Cannot recreate slice-contours - no slice widget');
            return;
        }

        console.log('[ViewLock] Recreating slice-contours with options:', options);

        // Ensure the slice contours checkbox is enabled
        const sliceContoursCheckbox = document.getElementById('sliceShowContours');
        if (sliceContoursCheckbox && !sliceContoursCheckbox.checked) {
            sliceContoursCheckbox.checked = true;
            // Show the contour settings panel
            const settings = document.getElementById('sliceContourSettings');
            if (settings) settings.style.display = 'block';
        }

        // Populate field dropdown BEFORE setting the value
        // This ensures the option exists when we try to set it
        if (sliceWidget.populateSliceContourFields) {
            console.log('[ViewLock] Populating slice contour fields before setting value');
            sliceWidget.populateSliceContourFields();
        }

        // Populate UI from options AFTER populating dropdowns
        if (options) {
            // Set plane position
            if (options.origin) {
                const originX = document.getElementById('slicePlaneOriginX');
                const originY = document.getElementById('slicePlaneOriginY');
                const originZ = document.getElementById('slicePlaneOriginZ');
                if (originX) originX.value = options.origin[0];
                if (originY) originY.value = options.origin[1];
                if (originZ) originZ.value = options.origin[2];
            }

            if (options.normal) {
                const normalX = document.getElementById('slicePlaneNormalX');
                const normalY = document.getElementById('slicePlaneNormalY');
                const normalZ = document.getElementById('slicePlaneNormalZ');
                if (normalX) normalX.value = options.normal[0];
                if (normalY) normalY.value = options.normal[1];
                if (normalZ) normalZ.value = options.normal[2];
            }

            // Set color field (after populating dropdown)
            const colorFieldSelect = document.getElementById('sliceContourField');
            if (colorFieldSelect && options.colorField) {
                // Check if the option exists in the dropdown
                const optionExists = Array.from(colorFieldSelect.options).some(opt => opt.value === options.colorField);
                if (optionExists) {
                    colorFieldSelect.value = options.colorField;
                    console.log('[ViewLock] Set slice contour field to:', options.colorField);
                } else {
                    console.warn('[ViewLock] Color field option not found:', options.colorField,
                        'Available:', Array.from(colorFieldSelect.options).map(o => o.value));
                    // Try to add the option if it doesn't exist
                    const newOption = document.createElement('option');
                    newOption.value = options.colorField;
                    newOption.textContent = options.colorField;
                    colorFieldSelect.appendChild(newOption);
                    colorFieldSelect.value = options.colorField;
                    console.log('[ViewLock] Added and set slice contour field to:', options.colorField);
                }
            }

            // Set colormap
            const colormapSelect = document.getElementById('sliceContourColormap');
            if (colormapSelect && options.colormap) {
                colormapSelect.value = options.colormap;
                console.log('[ViewLock] Set slice contour colormap to:', options.colormap);
            }
        }

        // Trigger slice contours generation
        try {
            // Enable the slice widget if not already enabled
            if (!sliceWidget.enabled) {
                console.log('[ViewLock] Enabling slice widget for contours recreation');
                const sliceCheckbox = document.getElementById('sliceFilterEnabled');
                if (sliceCheckbox) {
                    sliceCheckbox.checked = true;
                    sliceCheckbox.dispatchEvent(new Event('change'));
                }
                // Also enable directly in case the checkbox event doesn't work
                sliceWidget.enable();
                // Wait a brief moment for the widget to initialize
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            sliceWidget.forceContoursGeneration = true;
            console.log('[ViewLock] Set forceContoursGeneration = true');

            // Update the slice widget's plane position from options
            if (options.origin) {
                sliceWidget.planeOrigin.set(options.origin[0], options.origin[1], options.origin[2]);
            }
            if (options.normal) {
                sliceWidget.planeNormal.set(options.normal[0], options.normal[1], options.normal[2]);
            }

            // Update the visual plane to match the position
            if (sliceWidget.planeGroup) {
                sliceWidget.planeGroup.position.copy(sliceWidget.planeOrigin);
                // Update rotation to match normal
                const quaternion = new THREE.Quaternion();
                const up = new THREE.Vector3(0, 0, 1);  // Default plane normal in local space
                quaternion.setFromUnitVectors(up, sliceWidget.planeNormal);
                sliceWidget.planeGroup.setRotationFromQuaternion(quaternion);
            }

            // Call updateSliceBackend directly in geometry mode to avoid debounce
            console.log('[ViewLock] Calling updateSliceBackend/updateSlice. renderingMode:', this.viewer.renderingMode);
            console.log('[ViewLock] Slice widget state - enabled:', sliceWidget.enabled,
                'forceContoursGeneration:', sliceWidget.forceContoursGeneration,
                'origin:', sliceWidget.planeOrigin?.toArray(),
                'normal:', sliceWidget.planeNormal?.toArray());

            // Verify dropdown values are set correctly before calling backend
            const verifyFieldSelect = document.getElementById('sliceContourField');
            const verifyColormapSelect = document.getElementById('sliceContourColormap');
            console.log('[ViewLock] Dropdown values before backend call:',
                'field:', verifyFieldSelect?.value,
                'colormap:', verifyColormapSelect?.value,
                'expected field:', options.colorField,
                'expected colormap:', options.colormap);

            if (this.viewer.renderingMode === 'geometry') {
                // Pass cache_key for collaborative session cache lookup
                // If leader computed this slice, follower can fetch from cache
                const backendOptions = {};
                if (options.cache_key) {
                    backendOptions.cache_key = options.cache_key;
                    console.log('[ViewLock] Passing cache_key for cache lookup:', options.cache_key);
                }
                await sliceWidget.updateSliceBackend(backendOptions);
            } else {
                await sliceWidget.updateSlice();
            }
            console.log('[ViewLock] Slice-contours recreated successfully');
        } catch (err) {
            console.error('[ViewLock] Failed to recreate slice-contours:', err);
        }
    }

    /**
     * Recreate slice-vectors (velocity vectors on slice plane)
     */
    async recreateSliceVectors(id, options) {
        if (!this.viewer.filters) {
            console.warn('[ViewLock] Cannot recreate slice-vectors - no filters');
            return;
        }

        // Remove existing slice-vectors
        this.viewer.filters.removeFilter('slice-vectors');

        // Get the slice widget
        const sliceWidget = this.viewer.filters?.sliceWidget;
        if (!sliceWidget) {
            console.warn('[ViewLock] Cannot recreate slice-vectors - no slice widget');
            return;
        }

        console.log('[ViewLock] Recreating slice-vectors with options:', options);

        // Ensure the slice vectors checkbox is enabled
        const sliceVectorsCheckbox = document.getElementById('sliceShowVectors');
        if (sliceVectorsCheckbox && !sliceVectorsCheckbox.checked) {
            sliceVectorsCheckbox.checked = true;
            // Show the vector settings panel
            const settings = document.getElementById('sliceVectorSettings');
            if (settings) settings.style.display = 'block';
        }

        // Also ensure the slice widget is enabled
        const sliceCheckbox = document.getElementById('sliceFilterEnabled');
        if (sliceCheckbox && !sliceCheckbox.checked) {
            sliceCheckbox.checked = true;
            sliceWidget.enable();
        }

        try {
            // Set the plane origin and normal from options
            if (options.origin) {
                const origin = Array.isArray(options.origin) ? options.origin : [options.origin.x, options.origin.y, options.origin.z];
                sliceWidget.planeOrigin.set(origin[0], origin[1], origin[2]);
                this.setInputValue('slicePlaneOriginX', origin[0]);
                this.setInputValue('slicePlaneOriginY', origin[1]);
                this.setInputValue('slicePlaneOriginZ', origin[2]);
            }

            if (options.normal) {
                const normal = Array.isArray(options.normal) ? options.normal : [options.normal.x, options.normal.y, options.normal.z];
                sliceWidget.planeNormal.set(normal[0], normal[1], normal[2]).normalize();
                this.setInputValue('slicePlaneNormalX', normal[0]);
                this.setInputValue('slicePlaneNormalY', normal[1]);
                this.setInputValue('slicePlaneNormalZ', normal[2]);
            }

            // Populate vector UI settings
            if (options.vectorField) {
                const vectorFieldSelect = document.getElementById('sliceVectorField');
                if (vectorFieldSelect) {
                    this.ensureSelectOption(vectorFieldSelect, options.vectorField, options.vectorField);
                    vectorFieldSelect.value = options.vectorField;
                }
            }

            if (options.vectorScale !== undefined) {
                this.setInputValue('sliceVectorScale', options.vectorScale);
            }

            if (options.vectorDensity !== undefined) {
                this.setInputValue('sliceVectorDensity', options.vectorDensity);
            }

            if (options.colormap) {
                const colormapSelect = document.getElementById('sliceVectorColormap');
                if (colormapSelect) {
                    colormapSelect.value = options.colormap;
                }
            }

            if (options.vectorMode) {
                const modeSelect = document.getElementById('sliceVectorMode');
                if (modeSelect) {
                    modeSelect.value = options.vectorMode;
                }
            }

            // Force vectors generation
            sliceWidget.forceVectorsGeneration = true;

            // Call updateSliceBackend directly in geometry mode
            if (this.viewer.renderingMode === 'geometry') {
                await sliceWidget.updateSliceBackend();
            } else {
                await sliceWidget.updateSlice();
            }
            console.log('[ViewLock] Slice-vectors recreated successfully');
        } catch (err) {
            console.error('[ViewLock] Failed to recreate slice-vectors:', err);
        }
    }

    /**
     * Recreate slice-isolines (contour lines on slice plane)
     */
    async recreateSliceIsolines(id, options) {
        if (!this.viewer.filters) {
            console.warn('[ViewLock] Cannot recreate slice-isolines - no filters');
            return;
        }

        // Remove existing slice-isolines
        this.viewer.filters.removeFilter('slice-isolines');

        // Get the slice widget
        const sliceWidget = this.viewer.filters?.sliceWidget;
        if (!sliceWidget) {
            console.warn('[ViewLock] Cannot recreate slice-isolines - no slice widget');
            return;
        }

        console.log('[ViewLock] Recreating slice-isolines with options:', options);

        // Ensure the slice isolines checkbox is enabled
        const sliceIsolinesCheckbox = document.getElementById('sliceShowIsolines');
        if (sliceIsolinesCheckbox && !sliceIsolinesCheckbox.checked) {
            sliceIsolinesCheckbox.checked = true;
            // Show the isolines settings panel
            const settings = document.getElementById('sliceIsolinesSettings');
            if (settings) settings.style.display = 'block';
        }

        // Also ensure the slice widget is enabled
        const sliceCheckbox = document.getElementById('sliceFilterEnabled');
        if (sliceCheckbox && !sliceCheckbox.checked) {
            sliceCheckbox.checked = true;
            sliceWidget.enable();
        }

        try {
            // Set the plane origin and normal from options
            if (options.origin) {
                const origin = Array.isArray(options.origin) ? options.origin : [options.origin.x, options.origin.y, options.origin.z];
                sliceWidget.planeOrigin.set(origin[0], origin[1], origin[2]);
                this.setInputValue('slicePlaneOriginX', origin[0]);
                this.setInputValue('slicePlaneOriginY', origin[1]);
                this.setInputValue('slicePlaneOriginZ', origin[2]);
            }

            if (options.normal) {
                const normal = Array.isArray(options.normal) ? options.normal : [options.normal.x, options.normal.y, options.normal.z];
                sliceWidget.planeNormal.set(normal[0], normal[1], normal[2]).normalize();
                this.setInputValue('slicePlaneNormalX', normal[0]);
                this.setInputValue('slicePlaneNormalY', normal[1]);
                this.setInputValue('slicePlaneNormalZ', normal[2]);
            }

            // Populate isolines UI settings
            if (options.isolinesField) {
                const fieldSelect = document.getElementById('sliceIsolinesField');
                if (fieldSelect) {
                    this.ensureSelectOption(fieldSelect, options.isolinesField, options.isolinesField);
                    fieldSelect.value = options.isolinesField;
                }
            }

            if (options.numIsolines !== undefined) {
                this.setInputValue('sliceIsolinesCount', options.numIsolines);
            }

            if (options.lineWidth !== undefined) {
                this.setInputValue('sliceIsolinesWidth', options.lineWidth);
            }

            if (options.colormap) {
                const colormapSelect = document.getElementById('sliceIsolinesColormap');
                if (colormapSelect) {
                    colormapSelect.value = options.colormap;
                }
            }

            if (options.showLabels !== undefined) {
                const labelsCheckbox = document.getElementById('sliceIsolinesShowLabels');
                if (labelsCheckbox) {
                    labelsCheckbox.checked = options.showLabels;
                }
            }

            if (options.labelScale !== undefined) {
                this.setInputValue('sliceIsolinesLabelScale', options.labelScale);
            }

            // Force isolines generation
            sliceWidget.forceIsolinesGeneration = true;

            // Call updateSliceBackend directly in geometry mode
            if (this.viewer.renderingMode === 'geometry') {
                await sliceWidget.updateSliceBackend();
            } else {
                await sliceWidget.updateSlice();
            }
            console.log('[ViewLock] Slice-isolines recreated successfully');
        } catch (err) {
            console.error('[ViewLock] Failed to recreate slice-isolines:', err);
        }
    }

    /**
     * Populate slice streamline UI from options
     */
    populateSliceStreamlineUI(options) {
        if (!options) return;

        // Vector field
        const vectorFieldSelect = document.getElementById('sliceStreamlineVectorField');
        if (vectorFieldSelect && options.variable) {
            vectorFieldSelect.value = options.variable;
        }

        // Colormap
        const colormapSelect = document.getElementById('sliceStreamlineColormap');
        if (colormapSelect && options.colormap) {
            colormapSelect.value = options.colormap;
        }

        // Color field
        const colorFieldSelect = document.getElementById('sliceStreamlineColorField');
        if (colorFieldSelect && options.colorField) {
            colorFieldSelect.value = options.colorField;
        }

        // Number of streamlines
        const numStreamlines = document.getElementById('sliceStreamlineCount');
        if (numStreamlines && options.numStreamlines) {
            numStreamlines.value = options.numStreamlines;
        }
    }

    /**
     * Populate streamline UI from options
     */
    populateStreamlineUI(options) {
        if (!options) return;

        console.log('[ViewLock] Populating streamline UI with options:', options);

        // Seed type
        const seedTypeSelect = document.getElementById('streamlineSeedType');
        if (seedTypeSelect && options.seedType) {
            seedTypeSelect.value = options.seedType;
            seedTypeSelect.dispatchEvent(new Event('change'));
        }

        // Common parameters
        this.setInputValue('streamlineMaxLength', options.maxLength);
        this.setInputValue('streamlineStepSize', options.stepSize);

        // Vector field (Integrate Along)
        const vectorFieldSelect = document.getElementById('streamlineVectorField');
        if (vectorFieldSelect && options.variable) {
            // Ensure the option exists in the dropdown
            this.ensureSelectOption(vectorFieldSelect, options.variable, options.variable);
            vectorFieldSelect.value = options.variable;
            console.log('[ViewLock] Set vector field to:', options.variable);
        }

        // Colormap
        const colormapSelect = document.getElementById('streamlineColormap');
        if (colormapSelect && options.colormap) {
            colormapSelect.value = options.colormap;
        }

        // Color field (Color By)
        const colorFieldSelect = document.getElementById('streamlineColorField');
        if (colorFieldSelect && options.colorField) {
            // Ensure the option exists in the dropdown
            this.ensureSelectOption(colorFieldSelect, options.colorField, options.colorField);
            colorFieldSelect.value = options.colorField;
            console.log('[ViewLock] Set color field to:', options.colorField);
        }

        // Seed type specific parameters
        // First hide all seed control panels
        const pointControls = document.getElementById('streamlinePointControls');
        const lineControls = document.getElementById('streamlineLineControls');
        const planeControls = document.getElementById('streamlinePlaneControls');
        if (pointControls) pointControls.style.display = 'none';
        if (lineControls) lineControls.style.display = 'none';
        if (planeControls) planeControls.style.display = 'none';

        if (options.seedType === 'point') {
            console.log('[ViewLock] Setting point seed params:', {
                pointCenterX: options.pointCenterX,
                pointCenterY: options.pointCenterY,
                pointCenterZ: options.pointCenterZ,
                pointRadius: options.pointRadius,
                pointCount: options.pointCount
            });
            // Show the point seed controls
            if (pointControls) pointControls.style.display = 'block';

            this.setInputValue('streamlinePointCenterX', options.pointCenterX);
            this.setInputValue('streamlinePointCenterY', options.pointCenterY);
            this.setInputValue('streamlinePointCenterZ', options.pointCenterZ);
            this.setInputValue('streamlinePointRadius', options.pointRadius);
            this.setInputValue('streamlinePointCount', options.pointCount);
        } else if (options.seedType === 'line') {
            // Show line controls
            if (lineControls) lineControls.style.display = 'block';

            this.setInputValue('streamlineLineStartX', options.lineStartX);
            this.setInputValue('streamlineLineStartY', options.lineStartY);
            this.setInputValue('streamlineLineStartZ', options.lineStartZ);
            this.setInputValue('streamlineLineEndX', options.lineEndX);
            this.setInputValue('streamlineLineEndY', options.lineEndY);
            this.setInputValue('streamlineLineEndZ', options.lineEndZ);
            this.setInputValue('streamlineLineResolution', options.lineResolution);
        } else if (options.seedType === 'plane') {
            // Show plane controls
            if (planeControls) planeControls.style.display = 'block';

            this.setInputValue('streamlinePlaneOriginX', options.planeOriginX);
            this.setInputValue('streamlinePlaneOriginY', options.planeOriginY);
            this.setInputValue('streamlinePlaneOriginZ', options.planeOriginZ);
            this.setInputValue('streamlinePlaneNormalX', options.planeNormalX);
            this.setInputValue('streamlinePlaneNormalY', options.planeNormalY);
            this.setInputValue('streamlinePlaneNormalZ', options.planeNormalZ);
            this.setInputValue('streamlinePlaneWidth', options.planeWidth);
            this.setInputValue('streamlinePlaneHeight', options.planeHeight);
            this.setInputValue('streamlinePlaneResolutionU', options.planeResolutionU);
            this.setInputValue('streamlinePlaneResolutionV', options.planeResolutionV);
        }

        // Render mode and its specific parameters
        const renderModeSelect = document.getElementById('streamlineRenderMode');
        if (renderModeSelect && options.renderMode) {
            renderModeSelect.value = options.renderMode;
            renderModeSelect.dispatchEvent(new Event('change')); // Trigger UI update for mode-specific controls
        }

        // Tube parameters
        if (options.renderMode === 'tubes') {
            this.setInputValue('streamlineTubeRadius', options.tubeRadius);
            this.setInputValue('streamlineTubeSides', options.tubeSides);
            const varyRadiusSelect = document.getElementById('streamlineVaryRadius');
            if (varyRadiusSelect && options.varyRadius) {
                varyRadiusSelect.value = options.varyRadius;
            }
        }
        // Ribbon parameters
        else if (options.renderMode === 'ribbons') {
            this.setInputValue('streamlineRibbonWidth', options.ribbonWidth);
        }
        // Particle parameters
        else if (options.renderMode === 'particles') {
            this.setInputValue('streamlineParticleCount', options.particleCount);
            this.setInputValue('streamlineParticleSteps', options.particleSteps);
        }

        console.log('[ViewLock] Streamline UI population complete');
    }

    /**
     * Recreate slice filter
     */
    async recreateSlice(id, options) {
        if (!this.viewer.filters || !this.viewer.currentData) return;

        // Remove existing if present
        this.viewer.filters.removeFilter(id);

        // Populate UI inputs
        if (options.origin) {
            this.setInputValue('sliceOriginX', options.origin.x);
            this.setInputValue('sliceOriginY', options.origin.y);
            this.setInputValue('sliceOriginZ', options.origin.z);
        }
        if (options.normal) {
            this.setInputValue('sliceNormalX', options.normal.x);
            this.setInputValue('sliceNormalY', options.normal.y);
            this.setInputValue('sliceNormalZ', options.normal.z);
        }

        // Apply slice
        this.viewer.filters.applySliceFromUI();
    }

    /**
     * Recreate contour filter
     */
    async recreateContour(id, options) {
        if (!this.viewer.filters || !this.viewer.currentData) return;

        // Remove existing if present
        this.viewer.filters.removeFilter(id);

        // Ensure contour checkbox is enabled
        const contourCheckbox = document.getElementById('contourFilterEnabled');
        if (contourCheckbox && !contourCheckbox.checked) {
            contourCheckbox.checked = true;
            // Show controls panel
            const controls = document.getElementById('contourControls');
            if (controls) controls.style.display = 'flex';
        }

        // Populate UI inputs
        const contourField = document.getElementById('contourField');
        if (contourField && options.field) {
            // Check if the option exists in the dropdown
            const optionExists = Array.from(contourField.options).some(opt => opt.value === options.field);
            if (optionExists) {
                contourField.value = options.field;
            } else {
                // Add the option if it doesn't exist
                const newOption = document.createElement('option');
                newOption.value = options.field;
                newOption.textContent = options.field;
                contourField.appendChild(newOption);
                contourField.value = options.field;
            }
            console.log('[ViewLock] Set contour field to:', options.field, 'current value:', contourField.value);
        }

        // Set auto-spacing mode based on the captured autoSpacing flag
        const autoSpacingCheckbox = document.getElementById('contourAutoSpacing');
        const manualControls = document.getElementById('contourManualControls');
        const autoControls = document.getElementById('contourAutoControls');

        if (options.autoSpacing) {
            // Use auto-spacing mode
            if (autoSpacingCheckbox) autoSpacingCheckbox.checked = true;
            if (manualControls) manualControls.style.display = 'none';
            if (autoControls) autoControls.style.display = 'block';
            this.setInputValue('contourCount', options.numContours || 10);
        } else if (options.values && options.values.length > 0) {
            // Use manual values mode
            if (autoSpacingCheckbox) autoSpacingCheckbox.checked = false;
            if (manualControls) manualControls.style.display = 'block';
            if (autoControls) autoControls.style.display = 'none';
            this.setInputValue('contourValues', options.values.join(', '));
        }

        // Apply contour (pass cache_key for collaborative session cache lookup)
        const contourOptions = {};
        if (options.cache_key) {
            contourOptions.cache_key = options.cache_key;
            console.log('[ViewLock] Passing cache_key for contour cache lookup:', options.cache_key);
        }
        await this.viewer.filters.applyContourFromUI(contourOptions);
    }

    /**
     * Recreate glyph filter
     */
    async recreateGlyph(id, options) {
        if (!this.viewer.filters || !this.viewer.currentData) return;

        // Remove existing if present
        this.viewer.filters.removeFilter(id);

        // Populate UI inputs
        const glyphVectorField = document.getElementById('glyphVectorField');
        if (glyphVectorField && options.vectorField) {
            this.ensureSelectOption(glyphVectorField, options.vectorField, options.vectorField);
            glyphVectorField.value = options.vectorField;
        }

        const glyphType = document.getElementById('glyphType');
        if (glyphType && options.glyphType) {
            glyphType.value = options.glyphType;
        }

        this.setInputValue('glyphScaleFactor', options.scaleFactor);
        this.setInputValue('glyphSkipFactor', options.skipFactor);

        // Set scale mode dropdown
        const glyphScaleMode = document.getElementById('glyphScaleMode');
        if (glyphScaleMode && options.scaleMode) {
            glyphScaleMode.value = options.scaleMode;
        }

        // Set color field dropdown (for separate direction vs coloring)
        const glyphColorField = document.getElementById('glyphColorField');
        if (glyphColorField && options.colorField !== undefined) {
            // colorField may be empty string (meaning vector magnitude) or a field name
            this.ensureSelectOption(glyphColorField, options.colorField, options.colorField || '(Vector Magnitude)');
            glyphColorField.value = options.colorField;
        }

        // Set colormap dropdown
        const glyphColormap = document.getElementById('glyphColormap');
        if (glyphColormap && options.colormap) {
            glyphColormap.value = options.colormap;
        }

        // Set color mode
        const glyphColorMode = document.getElementById('glyphColorMode');
        if (glyphColorMode && options.colorMode) {
            glyphColorMode.value = options.colorMode;
        }

        // Apply glyph (pass cache_key and colorField for collaborative session cache lookup)
        const glyphOptions = {};
        if (options.cache_key) {
            glyphOptions.cache_key = options.cache_key;
            console.log('[ViewLock] Passing cache_key for glyph cache lookup:', options.cache_key);
        }
        // Pass colorField explicitly to avoid relying on dropdown which may not be populated
        if (options.colorField !== undefined) {
            glyphOptions.colorField = options.colorField;
        }
        await this.viewer.filters.applyGlyphFromUI(glyphOptions);
    }

    /**
     * Recreate Surface LIC filter
     */
    async recreateSurfaceLIC(id, options) {
        if (!this.viewer.filters || !this.viewer.currentData) return;

        // Remove existing if present
        this.viewer.filters.removeFilter(id);

        // Populate UI inputs
        const vectorFieldSelect = document.getElementById('surfaceLICVectorField');
        if (vectorFieldSelect && options.vectorField) {
            // Ensure option exists
            this.ensureSelectOption(vectorFieldSelect, options.vectorField, options.vectorField);
            vectorFieldSelect.value = options.vectorField;
        }

        this.setInputValue('surfaceLICKernelLength', options.kernelLength);
        const kernelLengthValue = document.getElementById('surfaceLICKernelLengthValue');
        if (kernelLengthValue && options.kernelLength) {
            kernelLengthValue.textContent = options.kernelLength;
        }

        this.setInputValue('surfaceLICNoiseResolution', options.noiseResolution);

        this.setInputValue('surfaceLICContrast', options.contrastEnhance);
        const contrastValue = document.getElementById('surfaceLICContrastValue');
        if (contrastValue && options.contrastEnhance) {
            contrastValue.textContent = options.contrastEnhance;
        }

        const colorModeSelect = document.getElementById('surfaceLICColorMode');
        if (colorModeSelect && options.colorMode) {
            colorModeSelect.value = options.colorMode;
        }

        const colormapSelect = document.getElementById('surfaceLICColormap');
        if (colormapSelect && options.colormap) {
            colormapSelect.value = options.colormap;
        }

        this.setInputValue('surfaceLICOpacity', options.opacity);
        const opacityValue = document.getElementById('surfaceLICOpacityValue');
        if (opacityValue && options.opacity) {
            opacityValue.textContent = options.opacity;
        }

        // Set animation state
        const animateCheckbox = document.getElementById('surfaceLICAnimate');
        if (animateCheckbox) {
            animateCheckbox.checked = options.animate || false;
            const animationControls = document.getElementById('surfaceLICAnimationControls');
            if (animationControls) {
                animationControls.style.display = options.animate ? 'block' : 'none';
            }
        }

        if (options.animationSpeed) {
            this.setInputValue('surfaceLICAnimationSpeed', options.animationSpeed);
            const speedValue = document.getElementById('surfaceLICAnimationSpeedValue');
            if (speedValue) {
                speedValue.textContent = options.animationSpeed;
            }
        }

        // Enable the Surface LIC checkbox and show controls
        const enabledCheckbox = document.getElementById('surfaceLICFilterEnabled');
        if (enabledCheckbox) {
            enabledCheckbox.checked = true;
        }
        const controls = document.getElementById('surfaceLICControls');
        if (controls) {
            controls.style.display = 'flex';
        }

        // Populate fields dropdown first
        if (this.viewer.filters.populateSurfaceLICFields) {
            this.viewer.filters.populateSurfaceLICFields();
        }

        // Set the vector field value again after population
        if (vectorFieldSelect && options.vectorField) {
            vectorFieldSelect.value = options.vectorField;
        }

        // Apply Surface LIC
        await this.viewer.filters.applySurfaceLICFromUI();
    }

    /**
     * Recreate Volume LIC filter
     */
    async recreateVolumeLIC(id, options) {
        if (!this.viewer.filters || !this.viewer.currentData) return;

        // Remove existing if present
        this.viewer.filters.removeFilter(id);

        // Populate UI inputs
        const vectorFieldSelect = document.getElementById('volumeLICVectorField');
        if (vectorFieldSelect && options.vectorField) {
            // Ensure option exists
            this.ensureSelectOption(vectorFieldSelect, options.vectorField, options.vectorField);
            vectorFieldSelect.value = options.vectorField;
        }

        const resolutionSelect = document.getElementById('volumeLICResolution');
        if (resolutionSelect && options.resolution) {
            resolutionSelect.value = options.resolution;
        }

        this.setInputValue('volumeLICKernelLength', options.kernelLength);
        const kernelLengthValue = document.getElementById('volumeLICKernelLengthValue');
        if (kernelLengthValue && options.kernelLength) {
            kernelLengthValue.textContent = options.kernelLength;
        }

        this.setInputValue('volumeLICStepSize', options.stepSize);
        const stepSizeValue = document.getElementById('volumeLICStepSizeValue');
        if (stepSizeValue && options.stepSize) {
            stepSizeValue.textContent = options.stepSize;
        }

        this.setInputValue('volumeLICContrast', options.contrastEnhance);
        const contrastValue = document.getElementById('volumeLICContrastValue');
        if (contrastValue && options.contrastEnhance) {
            contrastValue.textContent = options.contrastEnhance;
        }

        const colormapSelect = document.getElementById('volumeLICColormap');
        if (colormapSelect && options.colormap) {
            colormapSelect.value = options.colormap;
        }

        this.setInputValue('volumeLICOpacity', options.opacity);
        const opacityValue = document.getElementById('volumeLICOpacityValue');
        if (opacityValue && options.opacity) {
            opacityValue.textContent = options.opacity;
        }

        this.setInputValue('volumeLICThreshold', options.threshold);
        const thresholdValue = document.getElementById('volumeLICThresholdValue');
        if (thresholdValue && options.threshold) {
            thresholdValue.textContent = options.threshold;
        }

        this.setInputValue('volumeLICSteps', options.steps);
        const stepsValue = document.getElementById('volumeLICStepsValue');
        if (stepsValue && options.steps) {
            stepsValue.textContent = options.steps;
        }

        this.setInputValue('volumeLICBrightness', options.brightness);
        const brightnessValue = document.getElementById('volumeLICBrightnessValue');
        if (brightnessValue && options.brightness) {
            brightnessValue.textContent = options.brightness;
        }

        this.setInputValue('volumeLICWeight', options.licWeight);
        const weightValue = document.getElementById('volumeLICWeightValue');
        if (weightValue && options.licWeight) {
            weightValue.textContent = options.licWeight;
        }

        // Set animation state
        this.setInputValue('volumeLICAnimationSpeed', options.animationSpeed);
        const animSpeedValue = document.getElementById('volumeLICAnimationSpeedValue');
        if (animSpeedValue && options.animationSpeed) {
            animSpeedValue.textContent = options.animationSpeed;
        }

        const animatedCheckbox = document.getElementById('volumeLICAnimated');
        if (animatedCheckbox) {
            animatedCheckbox.checked = options.animated !== false; // Default to true
        }

        // Enable the Volume LIC checkbox and show controls
        const enabledCheckbox = document.getElementById('volumeLICFilterEnabled');
        if (enabledCheckbox) {
            enabledCheckbox.checked = true;
        }
        const controls = document.getElementById('volumeLICControls');
        if (controls) {
            controls.style.display = 'flex';
        }

        // Populate fields dropdown first
        if (this.viewer.filters.populateVolumeLICFields) {
            this.viewer.filters.populateVolumeLICFields();
        }

        // Set the vector field value again after population
        if (vectorFieldSelect && options.vectorField) {
            vectorFieldSelect.value = options.vectorField;
        }

        // Apply Volume LIC
        await this.viewer.filters.applyVolumeLICFromUI();
    }

    /**
     * Helper to set input value by ID
     */
    setInputValue(elementId, value) {
        if (value === undefined || value === null) return;
        const input = document.getElementById(elementId);
        if (input) {
            input.value = value;
        }
    }

    /**
     * Helper to ensure a select option exists, adding it if necessary
     */
    ensureSelectOption(selectElement, value, text) {
        if (!selectElement || !value) return;

        // Check if option already exists
        const exists = Array.from(selectElement.options).some(opt => opt.value === value);
        if (!exists) {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = text || value;
            selectElement.appendChild(option);
            console.log(`[ViewLock] Added missing option '${value}' to select`);
        }
    }

    /**
     * Apply mesh part visibility state
     */
    applyMeshVisibility(visibleParts) {
        if (!this.viewer.meshParts || !visibleParts) return;

        const visibleSet = new Set(visibleParts);
        console.log('[ViewLock] Applying mesh visibility:', visibleParts);

        // Update the visibleParts set to match controller's state
        this.viewer.meshParts.visibleParts.clear();
        visibleParts.forEach(partName => {
            this.viewer.meshParts.visibleParts.add(partName);
        });

        // Get the mesh object
        const meshObjectId = this.viewer.meshParts.activeMeshObjectId || 'grid-surface';
        const meshObject = this.viewer.engine?.objects?.get(meshObjectId) ||
                          this.viewer.engine?.objects?.get('main-mesh');

        if (!meshObject || !meshObject.object) {
            console.warn('[ViewLock] No mesh object found for visibility update');
            return;
        }

        const obj = meshObject.object;

        // Apply visibility directly to mesh parts
        if (obj.type === 'Group') {
            // Get all meshes in the group (including nested)
            const allMeshes = this.viewer.getAllMeshesInGroup ?
                             this.viewer.getAllMeshesInGroup(obj) :
                             obj.children;

            allMeshes.forEach(mesh => {
                const partName = mesh.userData?.partName;
                if (partName) {
                    const shouldBeVisible = visibleSet.has(partName);
                    mesh.visible = shouldBeVisible;
                    console.log(`[ViewLock] Set mesh part '${partName}' visible: ${shouldBeVisible}`);

                    // Update checkbox - internal mesh has special checkbox id 'partInternalMesh'
                    let checkbox;
                    const internalPartName = this.viewer.meshParts?.internalMeshPartName;
                    if (partName === 'internal' || partName === internalPartName) {
                        checkbox = document.getElementById('partInternalMesh');
                    } else {
                        checkbox = document.getElementById(`part${partName}`);
                    }
                    if (checkbox) {
                        checkbox.checked = shouldBeVisible;
                    }
                }
            });
        }

        // Update group checkboxes
        this.updateGroupCheckboxes();

        // Request render
        if (this.viewer.engine && this.viewer.engine.render) {
            this.viewer.engine.render();
        }
    }

    /**
     * Update group checkbox states based on their children
     */
    updateGroupCheckboxes() {
        document.querySelectorAll('.mesh-part-group').forEach(groupDiv => {
            try {
                const patches = JSON.parse(groupDiv.dataset.patches || '[]');
                const groupCheckbox = groupDiv.querySelector('.mesh-part-group-header input[type="checkbox"]');
                if (groupCheckbox && patches.length > 0) {
                    const allChecked = patches.every(name => {
                        const cb = document.getElementById(`part${name}`);
                        return cb && cb.checked;
                    });
                    groupCheckbox.checked = allChecked;
                }
            } catch (e) {
                // Ignore parsing errors
            }
        });
    }

    /**
     * Apply plot data from controller
     */
    async applyPlotData(plotData) {
        if (!plotData) return;

        console.log('[ViewLock] Applying plot data:', plotData.probe_type || plotData.probeType);
        console.log('[ViewLock] Plot data keys:', Object.keys(plotData));
        console.log('[ViewLock] Has points_2d:', !!plotData.points_2d, 'Has grids:', !!plotData.grids, 'Has values:', !!plotData.values);

        // Store the plot data
        window.currentPlotData = plotData;

        // Populate UI fields
        const fieldSelect = document.getElementById('xyPlotField');
        if (fieldSelect && plotData.field) {
            // Ensure the option exists
            this.ensureSelectOption(fieldSelect, plotData.field, plotData.field);
            fieldSelect.value = plotData.field;
        }

        const probeTypeSelect = document.getElementById('xyPlotProbeType');
        if (probeTypeSelect && plotData.probeType) {
            probeTypeSelect.value = plotData.probeType;
            probeTypeSelect.dispatchEvent(new Event('change'));
        }

        const numPointsInput = document.getElementById('xyPlotNumPoints');
        if (numPointsInput && plotData.numPoints) {
            numPointsInput.value = plotData.numPoints;
        }

        // Populate line probe coordinates
        if (plotData.lineStart) {
            this.setInputValue('probeLineStartX', plotData.lineStart.x);
            this.setInputValue('probeLineStartY', plotData.lineStart.y);
            this.setInputValue('probeLineStartZ', plotData.lineStart.z);
        }
        if (plotData.lineEnd) {
            this.setInputValue('probeLineEndX', plotData.lineEnd.x);
            this.setInputValue('probeLineEndY', plotData.lineEnd.y);
            this.setInputValue('probeLineEndZ', plotData.lineEnd.z);
        }

        // Populate slice plane parameters
        if (plotData.slicePlane) {
            this.setInputValue('slicePlaneNormalX', plotData.slicePlane.normalX);
            this.setInputValue('slicePlaneNormalY', plotData.slicePlane.normalY);
            this.setInputValue('slicePlaneNormalZ', plotData.slicePlane.normalZ);
            this.setInputValue('slicePlaneOriginX', plotData.slicePlane.originX);
            this.setInputValue('slicePlaneOriginY', plotData.slicePlane.originY);
            this.setInputValue('slicePlaneOriginZ', plotData.slicePlane.originZ);
            this.setInputValue('slicePlaneResolution', plotData.slicePlane.resolution);
        }

        // Apply probe points for point cloud mode
        if (plotData.probePoints && plotData.probePoints.length > 0 && this.viewer) {
            // Clear existing probe points first
            if (this.viewer.clearProbePoints) {
                this.viewer.clearProbePoints();
            }
            // Add each probe point (skipSync = true to avoid feedback loop)
            plotData.probePoints.forEach(point => {
                if (this.viewer.addProbePoint) {
                    this.viewer.addProbePoint(point, true);
                }
            });
            console.log('[ViewLock] Applied probe points:', plotData.probePoints.length);
        } else if (this.viewer?.probePoints?.length > 0) {
            // Controller has no probe points but we do - clear ours
            if (this.viewer.clearProbePoints) {
                this.viewer.clearProbePoints();
                console.log('[ViewLock] Cleared probe points (synced from controller)');
            }
        }

        // Open the modal if it was open on controller
        console.log('[ViewLock] Plot data modalOpen:', plotData.modalOpen, 'openChartModal exists:', typeof openChartModal === 'function');
        if (plotData.modalOpen && typeof openChartModal === 'function') {
            // Small delay to ensure data is ready
            console.log('[ViewLock] Opening chart modal for follower');
            setTimeout(() => {
                openChartModal();
            }, 100);
        }
    }

    /**
     * Apply integration data from controller
     */
    async applyIntegrationData(integrationData) {
        if (!integrationData) return;

        console.log('[ViewLock] Applying integration data:', integrationData.field_name);

        // Store the integration data
        window.currentIntegrationData = integrationData;

        // Populate UI fields
        const integrationEnabled = document.getElementById('integrationEnabled');
        if (integrationEnabled && integrationData.enabled) {
            integrationEnabled.checked = true;
            // Show controls
            const controls = document.getElementById('integrationControls');
            if (controls) controls.style.display = 'block';
        }

        const fieldSelect = document.getElementById('integrationField');
        if (fieldSelect && integrationData.field) {
            this.ensureSelectOption(fieldSelect, integrationData.field, integrationData.field);
            fieldSelect.value = integrationData.field;
        }

        const typeSelect = document.getElementById('integrationType');
        if (typeSelect && integrationData.integrationType) {
            typeSelect.value = integrationData.integrationType;
        }

        const surfaceSelect = document.getElementById('integrationSurface');
        if (surfaceSelect && integrationData.surfaceSelection) {
            surfaceSelect.value = integrationData.surfaceSelection;
        }

        // Display the results if they were visible
        if (integrationData.resultsVisible && this.viewer?.filters?.displayIntegrationResults) {
            this.viewer.filters.displayIntegrationResults(integrationData);
            // Show export button
            const exportBtn = document.getElementById('btnExportIntegrationCSV');
            if (exportBtn) exportBtn.style.display = 'inline-block';
        }

        console.log('[ViewLock] Integration data applied');
    }

    /**
     * Apply mass flow rate data from controller
     */
    async applyMassFlowData(massFlowData) {
        if (!massFlowData) return;

        console.log('[ViewLock] Applying mass flow rate data');

        // Store the data
        window.currentMassFlowData = massFlowData;

        // Populate UI fields
        const massFlowEnabled = document.getElementById('massFlowEnabled');
        if (massFlowEnabled && massFlowData.enabled) {
            massFlowEnabled.checked = true;
            const controls = document.getElementById('massFlowControls');
            if (controls) controls.style.display = 'block';
        }

        const velocityField = document.getElementById('massFlowVelocityField');
        if (velocityField && massFlowData.velocityField) {
            this.ensureSelectOption(velocityField, massFlowData.velocityField, massFlowData.velocityField);
            velocityField.value = massFlowData.velocityField;
        }

        const densityMode = document.getElementById('massFlowDensityMode');
        if (densityMode && massFlowData.densityMode) {
            densityMode.value = massFlowData.densityMode;
            // Show/hide appropriate density input
            const constDiv = document.getElementById('massFlowDensityConstant');
            const fieldDiv = document.getElementById('massFlowDensityField');
            if (massFlowData.densityMode === 'constant') {
                if (constDiv) constDiv.style.display = 'block';
                if (fieldDiv) fieldDiv.style.display = 'none';
            } else {
                if (constDiv) constDiv.style.display = 'none';
                if (fieldDiv) fieldDiv.style.display = 'block';
            }
        }

        const densityValue = document.getElementById('massFlowDensityValue');
        if (densityValue && massFlowData.densityValue) {
            densityValue.value = massFlowData.densityValue;
        }

        const densityFieldSelect = document.getElementById('massFlowDensityFieldSelect');
        if (densityFieldSelect && massFlowData.densityField) {
            this.ensureSelectOption(densityFieldSelect, massFlowData.densityField, massFlowData.densityField);
            densityFieldSelect.value = massFlowData.densityField;
        }

        const surfaceSelect = document.getElementById('massFlowSurface');
        if (surfaceSelect && massFlowData.surfaceSelection) {
            surfaceSelect.value = massFlowData.surfaceSelection;
        }

        // Display the results if they were visible
        if (massFlowData.resultsVisible && this.viewer?.filters?.displayMassFlowResults) {
            this.viewer.filters.displayMassFlowResults(massFlowData);
        }

        console.log('[ViewLock] Mass flow rate data applied');
    }

    /**
     * Apply force coefficients data from controller
     */
    async applyForceCoeffData(forceCoeffData) {
        if (!forceCoeffData) return;

        console.log('[ViewLock] Applying force coefficients data');

        // Store the data
        window.currentForceCoeffData = forceCoeffData;

        // Populate UI fields
        const forceCoeffEnabled = document.getElementById('forceCoeffEnabled');
        if (forceCoeffEnabled && forceCoeffData.enabled) {
            forceCoeffEnabled.checked = true;
            const controls = document.getElementById('forceCoeffControls');
            if (controls) controls.style.display = 'block';
        }

        const pressureField = document.getElementById('forceCoeffPressureField');
        if (pressureField && forceCoeffData.pressureField) {
            this.ensureSelectOption(pressureField, forceCoeffData.pressureField, forceCoeffData.pressureField);
            pressureField.value = forceCoeffData.pressureField;
        }

        const velocityField = document.getElementById('forceCoeffVelocityField');
        if (velocityField && forceCoeffData.velocityField) {
            this.ensureSelectOption(velocityField, forceCoeffData.velocityField, forceCoeffData.velocityField);
            velocityField.value = forceCoeffData.velocityField;
        }

        // Reference parameters
        const refVelocity = document.getElementById('forceCoeffRefVelocity');
        if (refVelocity && forceCoeffData.refVelocity) refVelocity.value = forceCoeffData.refVelocity;

        const refArea = document.getElementById('forceCoeffRefArea');
        if (refArea && forceCoeffData.refArea) refArea.value = forceCoeffData.refArea;

        const refDensity = document.getElementById('forceCoeffRefDensity');
        if (refDensity && forceCoeffData.refDensity) refDensity.value = forceCoeffData.refDensity;

        const refPressure = document.getElementById('forceCoeffRefPressure');
        if (refPressure && forceCoeffData.refPressure !== undefined) refPressure.value = forceCoeffData.refPressure;

        // Flow direction
        if (forceCoeffData.flowDirection) {
            const flowX = document.getElementById('forceCoeffFlowX');
            const flowY = document.getElementById('forceCoeffFlowY');
            const flowZ = document.getElementById('forceCoeffFlowZ');
            if (flowX) flowX.value = forceCoeffData.flowDirection[0];
            if (flowY) flowY.value = forceCoeffData.flowDirection[1];
            if (flowZ) flowZ.value = forceCoeffData.flowDirection[2];
        }

        // Lift direction
        if (forceCoeffData.liftDirection) {
            const liftX = document.getElementById('forceCoeffLiftX');
            const liftY = document.getElementById('forceCoeffLiftY');
            const liftZ = document.getElementById('forceCoeffLiftZ');
            if (liftX) liftX.value = forceCoeffData.liftDirection[0];
            if (liftY) liftY.value = forceCoeffData.liftDirection[1];
            if (liftZ) liftZ.value = forceCoeffData.liftDirection[2];
        }

        // Flip normals checkbox
        const flipNormals = document.getElementById('forceCoeffFlipNormals');
        if (flipNormals) {
            flipNormals.checked = forceCoeffData.flipNormals || false;
        }

        const surfaceSelect = document.getElementById('forceCoeffSurface');
        if (surfaceSelect && forceCoeffData.surfaceSelection) {
            surfaceSelect.value = forceCoeffData.surfaceSelection;
        }

        // Display the results if they were visible
        if (forceCoeffData.resultsVisible && this.viewer?.filters?.displayForceCoeffResults) {
            this.viewer.filters.displayForceCoeffResults(forceCoeffData);
        }

        console.log('[ViewLock] Force coefficients data applied');
    }

    /**
     * Apply heat transfer data from controller
     */
    async applyHeatTransferData(heatTransferData) {
        if (!heatTransferData) return;

        console.log('[ViewLock] Applying heat transfer data');

        // Store the data
        window.currentHeatTransferData = heatTransferData;

        // Populate UI fields
        const heatTransferEnabled = document.getElementById('heatTransferEnabled');
        if (heatTransferEnabled && heatTransferData.enabled) {
            heatTransferEnabled.checked = true;
            const controls = document.getElementById('heatTransferControls');
            if (controls) controls.style.display = 'block';
        }

        const fluxField = document.getElementById('heatTransferFluxField');
        if (fluxField && heatTransferData.fluxField) {
            this.ensureSelectOption(fluxField, heatTransferData.fluxField, heatTransferData.fluxField);
            fluxField.value = heatTransferData.fluxField;
        }

        const tempField = document.getElementById('heatTransferTempField');
        if (tempField && heatTransferData.tempField) {
            this.ensureSelectOption(tempField, heatTransferData.tempField, heatTransferData.tempField);
            tempField.value = heatTransferData.tempField;
        }

        const refTemp = document.getElementById('heatTransferRefTemp');
        if (refTemp && heatTransferData.refTemp) refTemp.value = heatTransferData.refTemp;

        const wallTemp = document.getElementById('heatTransferWallTemp');
        if (wallTemp && heatTransferData.wallTemp) wallTemp.value = heatTransferData.wallTemp;

        const surfaceSelect = document.getElementById('heatTransferSurface');
        if (surfaceSelect && heatTransferData.surfaceSelection) {
            surfaceSelect.value = heatTransferData.surfaceSelection;
        }

        // Display the results if they were visible
        if (heatTransferData.resultsVisible && this.viewer?.filters?.displayHeatTransferResults) {
            this.viewer.filters.displayHeatTransferResults(heatTransferData);
        }

        console.log('[ViewLock] Heat transfer data applied');
    }

    /**
     * Apply mesh cutter data from controller
     */
    async applyMeshCutterData(meshCutterData) {
        if (!meshCutterData) return;

        console.log('[ViewLock] Applying mesh cutter data:', meshCutterData);

        // Store the data
        window.currentMeshCutterData = meshCutterData.data || null;

        // Populate UI fields
        const meshCutterEnabled = document.getElementById('meshCutterEnabled');
        const controls = document.getElementById('meshCutterControls');

        if (meshCutterEnabled) {
            meshCutterEnabled.checked = !!meshCutterData.enabled;

            if (controls) {
                controls.style.display = meshCutterData.enabled ? 'block' : 'none';
            }
        }

        // IMPORTANT: Load the planes configuration FIRST (before enabling widget)
        // This ensures meshCutterPlanes is populated before widget.enable() syncs
        if (this.viewer?.filters) {
            // Set the planes data directly first
            this.viewer.filters.meshCutterPlanes = meshCutterData.planes || [];
            this.viewer.filters.renderCuttingPlanesList();
        }

        // Now enable/disable the CuttingPlaneWidget
        const widget = this.viewer?.filters?.cuttingPlaneWidget;
        if (widget) {
            if (meshCutterData.enabled && !widget.enabled) {
                // Widget needs to be enabled
                widget.enable();
            } else if (!meshCutterData.enabled && widget.enabled) {
                // Widget needs to be disabled
                widget.disable();
            } else if (meshCutterData.enabled && widget.enabled) {
                // Widget is already enabled - need to sync planes data
                // This handles the case where planes change while widget is already enabled
                console.log('[ViewLock] Widget already enabled, syncing planes:', meshCutterData.planes?.length || 0);
                widget.syncWithPlaneData();
            }

            // If widget is enabled, apply widget states and force clipping update
            if (widget.enabled && meshCutterData.widgetStates) {
                Object.keys(meshCutterData.widgetStates).forEach(planeId => {
                    const state = meshCutterData.widgetStates[planeId];
                    const planeWidget = widget.planeWidgets.get(planeId);
                    if (planeWidget) {
                        // Apply visibility state
                        if (state.visible !== undefined && planeWidget.visible !== state.visible) {
                            widget.setPlaneWidgetVisible(planeId, state.visible);
                        }
                        // Apply clipping active state
                        if (state.clippingActive !== undefined) {
                            planeWidget.clippingActive = state.clippingActive;
                        }
                    }
                });
                // Force clipping update after applying all states
                widget.updateClippingPlanes();
            }
        }

        // Handle cut mesh data if present
        if (meshCutterData.data && this.viewer?.filters) {
            this.viewer.filters.renderCutMesh(meshCutterData.data);
            this.viewer.filters.displayMeshCutterResults(meshCutterData.data);
        }

        console.log('[ViewLock] Mesh cutter data applied');
    }

    /**
     * Apply mesh eraser data from controller
     */
    async applyMeshEraserData(meshEraserData) {
        if (!meshEraserData) return;

        console.log('[ViewLock] Applying mesh eraser data:', meshEraserData.sequences?.length || 0, 'sequences');

        // Populate UI fields
        const meshEraserEnabled = document.getElementById('meshEraserEnabled');
        const controls = document.getElementById('meshEraserControls');

        if (meshEraserEnabled) {
            meshEraserEnabled.checked = !!meshEraserData.enabled;

            if (controls) {
                controls.style.display = meshEraserData.enabled ? 'block' : 'none';
            }
        }

        // Load the eraser configuration
        if (this.viewer?.filters) {
            this.viewer.filters.loadMeshEraserConfig({
                enabled: meshEraserData.enabled,
                brushRadiusPercent: meshEraserData.brushRadiusPercent,
                sequences: meshEraserData.sequences || []
            });
        }

        // Show/hide results
        const resultsDiv = document.getElementById('meshEraserResults');
        if (resultsDiv && meshEraserData.resultsVisible !== undefined) {
            resultsDiv.style.display = meshEraserData.resultsVisible ? '' : 'none';
        }

        console.log('[ViewLock] Mesh eraser data applied');
    }

    /**
     * Apply mesh solidify data from controller
     */
    async applyMeshSolidifyData(meshSolidifyData) {
        if (!meshSolidifyData) return;

        console.log('[ViewLock] Applying mesh solidify data, active:', meshSolidifyData.active);

        // Populate UI fields
        const meshSolidifyEnabled = document.getElementById('meshSolidifyEnabled');
        const controls = document.getElementById('meshSolidifyControls');
        const thicknessValue = document.getElementById('solidifyThicknessValue');
        const thicknessSlider = document.getElementById('solidifyThicknessSlider');
        const directionSelect = document.getElementById('solidifyDirection');

        if (meshSolidifyEnabled) {
            meshSolidifyEnabled.checked = !!meshSolidifyData.enabled;
            if (controls) {
                controls.style.display = meshSolidifyData.enabled ? 'block' : 'none';
            }
        }

        // Set thickness and direction UI values
        if (thicknessValue && meshSolidifyData.thickness != null) {
            thicknessValue.value = meshSolidifyData.thickness;
        }
        if (thicknessSlider && meshSolidifyData.thickness != null) {
            thicknessSlider.value = meshSolidifyData.thickness;
        }
        if (directionSelect && meshSolidifyData.direction) {
            directionSelect.value = meshSolidifyData.direction;
        }

        // Update the parts list checkboxes
        if (meshSolidifyData.enabled && this.viewer) {
            this.viewer.updateSolidifyPartsList();

            // Check the selected parts
            if (meshSolidifyData.selectedParts && meshSolidifyData.selectedParts.length > 0) {
                const selectedSet = new Set(meshSolidifyData.selectedParts);
                document.querySelectorAll('#solidifyPartsList input[type="checkbox"]').forEach(cb => {
                    cb.checked = selectedSet.has(cb.dataset.partName);
                });
            }
        }

        // Apply the solidify filter if it was active on the controller
        if (meshSolidifyData.active && this.viewer?.filters?.meshSolidify) {
            const thickness = meshSolidifyData.thickness || 1;
            const direction = meshSolidifyData.direction || 'both';
            const selectedParts = meshSolidifyData.selectedParts && meshSolidifyData.selectedParts.length > 0
                ? new Set(meshSolidifyData.selectedParts)
                : null;

            this.viewer.filters.meshSolidify.apply(thickness, direction, selectedParts);

            // Re-apply highlights for selected parts on the solidified mesh
            if (selectedParts && this.viewer._setSolidifyPartHighlight) {
                for (const partName of selectedParts) {
                    this.viewer._setSolidifyPartHighlight(partName, true);
                }
            }

            // Update results display
            const resultsDiv = document.getElementById('solidifyResults');
            if (resultsDiv && meshSolidifyData.resultsVisible) {
                resultsDiv.style.display = 'block';
            }

            // Enable download button
            const btnDownload = document.getElementById('btnDownloadSolidify');
            if (btnDownload) btnDownload.disabled = false;
        } else if (!meshSolidifyData.active && this.viewer?.filters?.meshSolidify?.active) {
            // Controller removed solidify, remove on follower too
            this.viewer.filters.meshSolidify.remove();
            const resultsDiv = document.getElementById('solidifyResults');
            if (resultsDiv) resultsDiv.style.display = 'none';
            const btnDownload = document.getElementById('btnDownloadSolidify');
            if (btnDownload) btnDownload.disabled = true;
        }

        console.log('[ViewLock] Mesh solidify data applied');
    }

    /**
     * Apply measurements from controller
     */
    async applyMeasurements(measurementsData) {
        if (!measurementsData || !this.viewer) return;

        console.log('[ViewLock] Applying measurements:', measurementsData.items?.length || 0);

        // Set UI state - sync enabled/disabled state
        const measurementEnabled = document.getElementById('measurementEnabled');
        const controls = document.getElementById('measurementControls');
        if (measurementEnabled) {
            measurementEnabled.checked = !!measurementsData.enabled;
            if (controls) {
                controls.style.display = measurementsData.enabled ? 'block' : 'none';
            }
        }

        const unitSelect = document.getElementById('measurementUnit');
        if (unitSelect && measurementsData.unit) {
            unitSelect.value = measurementsData.unit;
        }

        const showCheckbox = document.getElementById('measurementShow');
        if (showCheckbox) {
            showCheckbox.checked = measurementsData.visible !== false;
        }

        // Clear existing measurements first and reset counter for proper ID sync
        if (this.viewer.clearAllMeasurements) {
            this.viewer.clearAllMeasurements(true); // Reset counter
        }

        // Apply unit setting to viewer (important: before recreating measurements)
        if (measurementsData.unit && this.viewer.measurementUnit !== measurementsData.unit) {
            this.viewer.measurementUnit = measurementsData.unit;
        }

        // Recreate measurements if any
        if (measurementsData.items && measurementsData.items.length > 0) {
            for (const m of measurementsData.items) {
                console.log('[ViewLock] Recreating measurement:', m.type, m.id);
                try {
                    if (m.type === 'distance' && m.startPoint && m.endPoint) {
                        // Recreate distance measurement with controller's ID
                        this.viewer.visualizeMeasurement(m.startPoint, m.endPoint, m.id);
                    } else if (m.type === 'angle' && m.point1 && m.vertex && m.point2 && m.angle !== undefined) {
                        // Recreate angle measurement
                        this.viewer.visualizeAngleMeasurement(m.point1, m.vertex, m.point2, m.angle, m.id);
                    } else if (m.type === 'area' && m.points && m.points.length >= 3 && m.area !== undefined) {
                        // Recreate area measurement
                        this.viewer.visualizeAreaMeasurement(m.points, m.area, m.id);
                    } else if (m.type === 'volume' && m.points && m.points.length >= 4 && m.volume !== undefined) {
                        // Recreate volume measurement
                        this.viewer.visualizeVolumeMeasurement(m.points, m.volume, m.id);
                    } else {
                        console.warn('[ViewLock] Measurement type not matched or missing data:', m.type,
                            'point1:', !!m.point1, 'vertex:', !!m.vertex, 'point2:', !!m.point2, 'angle:', m.angle);
                    }
                } catch (err) {
                    console.error('[ViewLock] Error recreating measurement:', m.type, m.id, err);
                }
            }
        }

        // Always update UI list (handles both populated and cleared states)
        if (this.viewer.updateMeasurementsList) {
            this.viewer.updateMeasurementsList();
        }

        console.log('[ViewLock] Measurements applied:', measurementsData.items?.length || 0, 'unit:', measurementsData.unit);
    }

    /**
     * Apply annotations state from controller
     */
    async applyAnnotations(annotationsData) {
        if (!annotationsData || !this.viewer || !this.viewer.annotationRenderer) return;

        console.log('[ViewLock] Applying annotations:', annotationsData.items?.length || 0);

        // Set UI state - sync enabled/disabled state
        const annotationsEnabled = document.getElementById('annotationsEnabled');
        const controls = document.getElementById('annotationsControls');
        if (annotationsEnabled) {
            annotationsEnabled.checked = !!annotationsData.enabled;
            if (controls) {
                controls.style.display = annotationsData.enabled ? 'block' : 'none';
            }
        }

        // Clear existing annotations first
        this.viewer.annotationRenderer.clearAll();

        // Recreate annotations if any
        if (annotationsData.items && annotationsData.items.length > 0) {
            for (const a of annotationsData.items) {
                console.log('[ViewLock] Recreating annotation:', a.type, a.id);
                try {
                    if (a.type === 'text' && a.position) {
                        this.viewer.annotationRenderer.addTextAnnotation({
                            id: a.id,
                            type: 'text',
                            text: a.text,
                            position: a.position,
                            font_size: a.font_size,
                            color: a.color,
                            background_color: a.background_color,
                            background_opacity: a.background_opacity
                        });
                    } else if (a.type === 'arrow' && a.start_point && a.end_point) {
                        this.viewer.annotationRenderer.addArrowAnnotation({
                            id: a.id,
                            type: 'arrow',
                            start_point: a.start_point,
                            end_point: a.end_point,
                            text: a.text,
                            arrow_size: a.arrow_size,
                            color: a.color,
                            line_width: a.line_width
                        });
                    } else if (a.type === 'scale_bar') {
                        this.viewer.annotationRenderer.addScaleBar({
                            id: a.id,
                            type: 'scale_bar',
                            length: a.length,
                            unit: a.unit,
                            position: a.position,
                            num_divisions: a.num_divisions
                        });
                    } else if (a.type === 'coordinate_axes' && a.origin) {
                        this.viewer.annotationRenderer.addCoordinateAxes({
                            id: a.id,
                            type: 'coordinate_axes',
                            origin: a.origin,
                            length: a.length,
                            labels: a.labels,
                            colors: a.colors
                        });
                    } else {
                        console.warn('[ViewLock] Annotation type not matched or missing data:', a.type);
                    }
                } catch (err) {
                    console.error('[ViewLock] Error recreating annotation:', a.type, a.id, err);
                }
            }
        }

        // Update UI list
        if (this.viewer.updateAnnotationsList) {
            this.viewer.updateAnnotationsList();
        }

        console.log('[ViewLock] Annotations applied:', annotationsData.items?.length || 0);
    }

    /**
     * Call this when making visualization changes while controlling
     * (e.g., adding streamline, slice, changing field)
     * @param {boolean} forceImmediate - If true, bypass throttle and send immediately
     */
    onVisualizationChange(forceImmediate = false) {
        // Don't broadcast if we're currently applying received state (prevents infinite loop)
        if (this.isApplyingState) {
            console.log('[ViewLock] Skipping broadcast - applying received state');
            return;
        }
        if (this.amIController) {
            this.broadcastFullState(forceImmediate);
        }
    }

    /**
     * Handle user leaving - update authorization state
     */
    onUserLeft(userId) {
        // Remove from session users
        this.sessionUsers.delete(userId);

        // If the lock holder left, clear the lock and release followers
        if (this.lockHolder && this.lockHolder.user_id === userId) {
            console.log('[ViewLock] Controller left - releasing sync');
            this.isLocked = false;
            this.amIController = false;
            this.amIFollowing = false;
            this.lockHolder = null;

            // Restore normal preload priority
            if (this.viewer?.geometryPreloader) {
                this.viewer.geometryPreloader.setPriority('normal');
            }
            // Also restore geometry cache preload delay
            if (this.viewer?.geometryCache) {
                this.viewer.geometryCache.preloadDelay = 200;
            }

            Utils.notify('Controller disconnected - you now have independent control', 'info');
            this.updateUI();
        }

        // If I have a pending request and this user was in the approval list
        if (this.myPendingRequest && this.myPendingRequest.approvals) {
            if (this.myPendingRequest.approvals[userId] !== undefined) {
                // User left - treat as auto-approve (they're no longer blocking)
                this.myPendingRequest.approvals[userId] = 'approved';
                this.updateRequestingOverlay();
                this.updateAuthDropdown();
                Utils.notify(`${this.sessionUsers.get(userId)?.user_name || 'User'} left - auto-approved`, 'info');
            }
        }

        // Remove any incoming requests from this user
        for (const [requestId, request] of this.incomingRequests) {
            if (request.user_id === userId) {
                this.incomingRequests.delete(requestId);
            }
        }
        this.updateApprovalDialog();
    }

    /**
     * Cleanup
     */
    destroy() {
        this.stopCameraBroadcast();
        this.clearMyPendingRequest();
        this.hideApprovalDialog();
        this.hideAuthDropdown();

        if (this.toolbar) {
            this.toolbar.style.display = 'none';
        }

        // Remove dynamically created elements
        if (this.approvalDialog && this.approvalDialog.parentNode) {
            this.approvalDialog.parentNode.removeChild(this.approvalDialog);
        }
        if (this.requestingOverlay && this.requestingOverlay.parentNode) {
            this.requestingOverlay.parentNode.removeChild(this.requestingOverlay);
        }
        if (this.authDropdown && this.authDropdown.parentNode) {
            this.authDropdown.parentNode.removeChild(this.authDropdown);
        }
    }
}

// Export
window.ViewLockManager = ViewLockManager;
